import { UserAgentApplication, Logger, LogLevel } from "msal";
import axios from "axios";
import utils from "utils";
import keys from "configs/constants";
import { Store as notifyStore } from "react-notifications-component";
import { tempOptions, NotifyContent } from "components/Notify";
import { store as reduxStore } from "index";
import { removeAlert } from "../redux/actions/index";
import { seedWifiPoints } from "@redux/selectors/wifi";
import { endRumSession } from "services/dynatraceRum";
import { forgetStoredOrgFilter } from "contexts/orgFilter";

const BASE_API_URL = keys.API_URL;
const API_VERSION = "/api/v1/";

const DEFAULT_LIMIT = 25;

const API_GET = "API_GET";
const API_POST = "API_POST";
const API_PUT = "API_PUT";
const API_PATCH = "API_PATCH";
const API_DELETE = "API_DELETE";

const msalConfig = {
  auth: {
    clientId: keys.B2C_APPID,
    authority: `https://${keys.B2C_TENANT}.b2clogin.com/tfp/${keys.B2C_TENANT}.onmicrosoft.com/${keys.B2C_SIGNUP_SIGNIN_POLICY}`,
    validateAuthority: false,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  scopes: [keys.B2C_SCOPE],
  cache: {
    cacheLocation: "sessionStorage", // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" to save cache in cookies
  },
  system: {
    logger: new Logger(
      (logLevel, message) => {
        switch (logLevel) {
          case LogLevel.Error:
            utils.debug(message, utils.MSG_TYPE_ERR);
            return;
          case LogLevel.Warning:
            utils.debug(message, utils.MSG_TYPE_WARN);
            return;
          case LogLevel.Info:
          case LogLevel.Verbose:
          default:
            return;
        }
      },
      {
        level: LogLevel.Verbose,
        piiLoggingEnabled: false,
        correlationId: "1234",
      }
    ),
  },
};

export const ResetPasswordPolicy = `https://${keys.B2C_TENANT}.b2clogin.com/tfp/${keys.B2C_TENANT}.onmicrosoft.com/${keys.B2C_PASSWORD_RESET_POLICY}`;
export const UpdateUserProfilePolicy = `https://${keys.B2C_TENANT}.b2clogin.com/tfp/${keys.B2C_TENANT}.onmicrosoft.com/${keys.B2C_PROFILE_EDIT}`;

export const msalAuth = new UserAgentApplication(msalConfig);

const TOKEN_REFRESH_LIFETIME = 300 * 1000; // Seconds
var authHeader = null;
var tokenExpiredOn = 0;

export const AUTH_LOGOUT = "logout";
export const AUTH_PENDING = "pending";

// Returned by callAPI/callAPIWithCancel when there is no usable auth header
// (redirect in flight, or signed out). It is shaped like an axios response
// because that is what every call site expects to receive; status 0 is never a
// real HTTP status, so `status !== 200` checks degrade on their own instead of
// dereferencing `undefined`. A fresh object each time: call sites mutate it.
export const AUTH_NOT_READY_STATUS = 0;
export const authNotReadyResponse = () => ({
  status: AUTH_NOT_READY_STATUS,
  data: null,
});

export const isAuthReady = (config) =>
  config !== null &&
  config !== undefined &&
  config !== AUTH_LOGOUT &&
  config !== AUTH_PENDING;

export const getAPICallAuthHeader = async (config = null) => {
  const now = new Date().getTime();

  if (tokenExpiredOn !== 0 && now >= new Date(tokenExpiredOn).getTime()) {
    msalAuth.logout({});
    return "logout";
  }

  if (
    tokenExpiredOn > 0 &&
    new Date(tokenExpiredOn).getTime() - now <= TOKEN_REFRESH_LIFETIME
  )
    clearSessionStorage();

  if (tokenExpiredOn === 0 || authHeader === null) {
    if (config !== null) authHeader = config;
    else {
      let i = 0;
      let acquired = null;
      while (acquired === null && i < 10) {
        acquired = await acquireToken();
        i++;
      }
      // Only a real auth header is cached. AUTH_PENDING means a redirect is in
      // flight and MSAL may never navigate (v1 no-ops when an interaction is
      // already in progress), so caching it here would make every later call
      // replay "pending" forever with no way back.
      if (!isAuthReady(acquired)) return acquired;
      authHeader = acquired;
    }
  }

  if (isAuthReady(authHeader)) authHeader["validateStatus"] = false;
  return authHeader;
};

export const acquireToken = async () => {
  return new Promise((resolve, reject) => {
    msalAuth
      .acquireTokenSilent(msalConfig)
      .then((accessToken) => {
        tokenExpiredOn = accessToken.expiresOn;
        utils.debug("Expires at " + new Date(tokenExpiredOn).toLocaleString());
        resolve({
          headers: {
            Authorization: "Bearer " + accessToken.accessToken,
          },
        });
      })
      .catch(async (error) => {
        if (error.errorCode === "token_renewal_error") {
          clearSessionStorage();
          resolve(acquireToken());
        } else if (error.errorCode === "interaction_required") {
          await msalAuth.acquireTokenRedirect(msalConfig);
          resolve(AUTH_PENDING);
        } else {
          // Error not managed, reject
          utils.debug(error, utils.MSG_TYPE_WARN);
          reject(error);
        }
      });
  });
};

export const clearSessionStorage = () => {
  tokenExpiredOn = 0;
  authHeader = null;
  Object.keys(sessionStorage)
    .filter((x) => x.indexOf("authority") > 0)
    .forEach((x) => sessionStorage.removeItem(x));
};

// The backend tags the 401 it returns for a server-side session revocation (password changed on another
// device, or logout) with these two headers -- see XpCommon.SessionRevocation. Every other 401 (expired
// or invalid bearer token, APIM subscription key, endpoint-level authorization) means something else and
// must NOT sign the user out, so it is left to the normal error path.
//
// NOTE: both headers have to be listed in the API's `Access-Control-Expose-Headers`. The web app and the
// API are on different hosts, and neither header is CORS-safelisted, so without that the browser hides
// them from JS and isSessionRevoked() silently always returns false.
const SESSION_REVOKED_HEADER = "x-session-revoked";
const AUTH_CHALLENGE_HEADER = "www-authenticate";

// HTTP header names are case-insensitive (RFC 9110). A fetch Response exposes a Headers instance whose
// get() already honours that; axios lower-cases the keys of its plain object, but that is an axios
// implementation detail, so match case-insensitively rather than depend on it. `name` is lower case.
const readResponseHeader = (response, name) => {
  const headers = response?.headers;
  if (!headers) return null;
  if (typeof headers.get === "function") return headers.get(name);
  const key = Object.keys(headers).find((k) => k.toLowerCase() === name);
  return key === undefined ? null : headers[key];
};

// RFC 7235 allows optional whitespace around the '=' of an auth-param (BWS) and treats the param name
// as case-insensitive, so be permissive about the shape of the challenge. The value is our own fixed
// token, but tolerate its casing too rather than depend on how the middleware spells it.
//
// Keep the two \s* separated by something that can never be whitespace: an earlier version had
// `\s*"?\s*`, which collapses to `\s*\s*` when the quote is absent and backtracks super-linearly on a
// long run of spaces that does not end in the token (SonarQube javascript:S8786). BWS is only allowed
// around the '=', never inside the quoted-string, so that third \s* was wrong anyway.
const SESSION_REVOKED_CHALLENGE = /error_description\s*=\s*"?session_revoked/i;

export const isSessionRevoked = (response) =>
  String(readResponseHeader(response, SESSION_REVOKED_HEADER) ?? "")
    .trim()
    .toLowerCase() === "true" ||
  SESSION_REVOKED_CHALLENGE.test(readResponseHeader(response, AUTH_CHALLENGE_HEADER) ?? "");

let isSigningOutAfterRevocation = false;

// Runs one step of the forced sign-out in isolation. Every step is best-effort cleanup: localStorage
// can throw (Safari private mode, quota), endRumSession() is a third-party SDK, and msalAuth.logout()
// touches storage and navigates. None of them may prevent the steps that follow, and none may escape
// this module -- handleSessionRevoked() is called from inside request handlers, so a thrown error
// would surface as an unrelated failure on whatever call happened to hit the revoked 401 first
// (a spurious error toast in callAPI, a rejected export in auditService, the wrong rejection shape
// in callCoreIAM). Returns whether the step succeeded.
const attemptSignOutStep = (label, step) => {
  try {
    step();
    return true;
  } catch (error) {
    utils.debug("warn", `backendService -- forced sign-out: ${label} failed`, error);
    return false;
  }
};

// Called when the backend rejects a call because this session was revoked server-side. Mirrors the
// interactive sign-out (withADB2CAuth.onSignOut) minus the revoke call, which would be pointless here:
// the session is already revoked. Guarded so concurrent revoked 401s trigger a single logout.
export const handleSessionRevoked = () => {
  if (isSigningOutAfterRevocation) return;
  isSigningOutAfterRevocation = true;
  utils.debug("backendService -- session revoked server-side, forcing sign-out");

  // Local state first, so the cached token and auth header are gone even if the redirect below fails.
  attemptSignOutStep("clearSessionStorage", clearSessionStorage);
  attemptSignOutStep("clear tempLanguage", () =>
    localStorage.removeItem("tempLanguage")
  );
  attemptSignOutStep("forget the organization filter", forgetStoredOrgFilter);
  attemptSignOutStep("endRumSession", endRumSession);

  // If the redirect itself failed the user is still signed in, so release the guard and let the next
  // revoked 401 retry. Every call is already rejecting, so no request proceeds with a dead session.
  if (!attemptSignOutStep("msalAuth.logout", () => msalAuth.logout())) {
    isSigningOutAfterRevocation = false;
  }
};

// ---------------------------------------------------------------------------
// Paged listings
//
// _getWithIteration reads page 1 to learn `total`, then fetches the remaining
// pages in parallel. Failure contract -- the same for every caller:
//
//   - All of the list, or a failure. A partial list is never returned: the
//     result is assembled only once every page has arrived.
//   - A later page that fails for good REJECTS the walk with that page's
//     failure (the axios response for an HTTP status, the axios error when
//     there was no response), exactly like a failed page 1 does. A 2xx page
//     without a list counts as failed too.
//   - Before that, a page whose failure looks transient is retried on its own
//     (see pageAttemptsAllowed); the other pages are not re-fetched. Page 1
//     too: one dropped request on a slow connection must not fail the listing.
//   - An unready token on any page (callAPI's authNotReadyResponse()) answers
//     authNotReadyResponse() for the whole walk, unretried, as page 1 does.
//   - One alert for the walk, raised on the final failure, unless the caller
//     passed skipAlert. None for a session-revoked 401: callAPI has already
//     sent that one down the sign-out path.
// ---------------------------------------------------------------------------

// Each value is the wait before one more attempt, so 3 attempts in all. Jittered
// so the parallel pages of one walk do not retry in lockstep. Worst case adds
// about 1.2 s before a failure surfaces, and only on pages that are failing.
const PAGE_RETRY_DELAYS_MS = [300, 900];
const PAGE_RETRY_JITTER = 0.2; // +/- 20%
const PAGE_MAX_ATTEMPTS = PAGE_RETRY_DELAYS_MS.length + 1;
// A 401 that is not a revocation gets one more try: each callAPI attempt
// re-reads the auth header, so a token refreshed mid-walk can succeed.
const PAGE_MAX_ATTEMPTS_UNAUTHORIZED = 2;
// Ceiling on a 429's Retry-After. Retry-After is not a CORS-safelisted header:
// unless the API lists it in Access-Control-Expose-Headers the browser hides
// it, and the backoff above applies instead.
const PAGE_RETRY_AFTER_MAX_MS = 5000;

const PAGE_AUTH_NOT_READY = Symbol("pageAuthNotReady");

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// callAPI rejects with the axios response for an HTTP status (validateStatus is
// off) and with the axios error when there was no response at all.
const failureStatus = (failure) =>
  typeof failure?.status === "number" ? failure.status : null;

const isRevokedFailure = (failure) =>
  failureStatus(failure) === 401 && isSessionRevoked(failure);

// How many attempts a page that failed like this one deserves in all.
const pageAttemptsAllowed = (failure) => {
  const status = failureStatus(failure);
  // No response: network error or timeout.
  if (status === null) return PAGE_MAX_ATTEMPTS;
  if (status === 401)
    return isSessionRevoked(failure) ? 1 : PAGE_MAX_ATTEMPTS_UNAUTHORIZED;
  if (status === 408 || status === 429 || status >= 500)
    return PAGE_MAX_ATTEMPTS;
  // 400, 403, 404, ... and a 2xx without a list: asking again changes nothing.
  return 1;
};

// Retry-After is either delay-seconds or an HTTP date (RFC 9110 10.2.3).
const retryAfterMs = (response) => {
  const value = String(readResponseHeader(response, "retry-after") ?? "").trim();
  if (value === "") return null;
  const seconds = Number(value);
  const ms = Number.isFinite(seconds)
    ? seconds * 1000
    : Date.parse(value) - Date.now();
  if (!Number.isFinite(ms)) return null;
  return Math.min(Math.max(ms, 0), PAGE_RETRY_AFTER_MAX_MS);
};

const pageRetryDelayMs = (failure, attempt) => {
  if (failureStatus(failure) === 429) {
    const wait = retryAfterMs(failure);
    if (wait !== null) return wait;
  }
  const base =
    PAGE_RETRY_DELAYS_MS[Math.min(attempt, PAGE_RETRY_DELAYS_MS.length) - 1];
  // Jitter only spreads retries out; nothing depends on it being unpredictable.
  return base * (1 - PAGE_RETRY_JITTER + Math.random() * 2 * PAGE_RETRY_JITTER); // NOSONAR
};

// Runs `request` until it answers, asking again after a backoff while its
// failure looks transient (see pageAttemptsAllowed). `shouldStop` turns true
// once nobody will read the result, and ends the retries early. For idempotent
// GETs only.
const retryTransient = async (request, label, shouldStop = () => false) => {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await request();
    } catch (error_) {
      if (shouldStop() || attempt >= pageAttemptsAllowed(error_)) throw error_;
      utils.debug(
        "warn",
        `backendService -- ${label} attempt ${attempt} failed, retrying`,
        error_
      );
      await sleep(pageRetryDelayMs(error_, attempt));
      if (shouldStop()) throw error_;
    }
  }
};

// A page of a walk; callAPI's own alert is held back (one alert per walk,
// raised by _getWithIteration).
const fetchPage = (url, shouldStop) =>
  retryTransient(
    () => callAPI(url, API_GET, null, null, true),
    `page ${url}`,
    shouldStop
  );

// One later page of a walk. `walk.settled` turns true once another page has
// failed the walk, so this one stops retrying for a result nobody will read.
const fetchPageWithRetry = async (url, walk) => {
  const page = await fetchPage(url, () => walk.settled);
  if (page.status === AUTH_NOT_READY_STATUS) throw PAGE_AUTH_NOT_READY;
  if (!Array.isArray(page.data?.data)) throw page;
  return page;
};

// Same wording as callAPI's own alert for the same failure.
const notifyPagedListFailure = (failure) => {
  const status = failureStatus(failure);
  // A 2xx page without a list is thrown as the response itself, so `data` may
  // be an object: never print it as "[object Object]".
  const detail = [failure?.data?.detail, failure?.data, failure?.statusText].find(
    (candidate) => typeof candidate === "string" && candidate !== ""
  );
  const message =
    status === null
      ? failure?.message ?? String(failure)
      : `${detail ?? "Request failed"} - ${status}`;
  notifyStore?.addNotification({
    ...tempOptions,
    content: NotifyContent("danger", null, message),
  });
};

// Pages after the first: none for an empty list, none past the end when total
// is an exact multiple of limit, none when total is missing or not a number.
const extraPageCount = (total, limit) =>
  Number.isFinite(total) && total > limit ? Math.ceil(total / limit) - 1 : 0;

// Page 1 of a walk: the same transient retries as a later page, so a failure
// retried away says nothing. The alert is raised once, on the final failure,
// unless the caller passed skipAlert (and never for a session-revoked 401,
// already being signed out).
const fetchFirstPageWithRetry = async (url, skipAlert) => {
  try {
    return await fetchPage(url);
  } catch (error_) {
    if (!skipAlert && !isRevokedFailure(error_)) notifyPagedListFailure(error_);
    throw error_;
  }
};

const _getWithIteration = async (base, limit = DEFAULT_LIMIT, skipAlert = false) => {
  // No need for limit parameter if limit is DEFAULT_LIMIT
  let url =
    limit === DEFAULT_LIMIT
      ? base
      : base.includes("?")
        ? base + "&limit=" + limit
        : base + "?limit=" + limit;

  // Call the first time and get total expected data
  const first = await fetchFirstPageWithRetry(url, skipAlert);

  // No auth, no first page: nothing to paginate over.
  if (!first || first.data === null) return first;

  const urls = [];
  for (let page = 1; page <= extraPageCount(first.data.total, limit); page += 1) {
    const offset = "offset=" + page * limit;
    urls.push(url + (url.includes("?") ? "&" : "?") + offset);
  }
  if (urls.length === 0) return first;

  const walk = { settled: false };
  let pages;
  try {
    pages = await Promise.all(urls.map((pageUrl) => fetchPageWithRetry(pageUrl, walk)));
  } catch (error_) {
    walk.settled = true;
    if (error_ === PAGE_AUTH_NOT_READY) return authNotReadyResponse();
    utils.debug("warn", "backendService -- paged listing failed", error_);
    if (!skipAlert && !isRevokedFailure(error_)) notifyPagedListFailure(error_);
    throw error_;
  }

  return {
    ...first,
    data: {
      ...first.data,
      data: first.data.data.concat(...pages.map((page) => page.data.data)),
    },
  };
};

// Raised by the initial-state wrappers below when a listing has no list to
// build from: an unready token ({ status: 0, data: null }), or a page that
// arrived without one. They run before any store dispatch, so failing here
// leaves the store as it was -- instead of a TypeError on `.data.data`, or an
// empty store that would read as "no sites".
export class ListingUnavailableError extends Error {
  constructor(listing, response) {
    super(`${listing} listing unavailable (status ${response?.status ?? "none"})`);
    this.name = "ListingUnavailableError";
    this.status = response?.status;
  }
}

const requireList = (list, listing, response) => {
  if (!Array.isArray(list)) throw new ListingUnavailableError(listing, response);
  return list;
};

// skipAlert: no error toast (the organization filter asks once per organization).
const getAppConfigurationsAsync = async (feature = null, customerGuid = null, skipAlert = false) => {
  let url = `${BASE_API_URL}${API_VERSION}features`;

  if (feature !== null)
    url += `?featureName=${feature}`;

  if (customerGuid !== null)
    url += `${feature === null ? "?" : "&"}customerGuid=${customerGuid}`;

  const response = await callAPI(url, API_GET, null, null, skipAlert);

  if (response.status !== 200) {
    return [];
  }

  return response.data;
}

const getConfigurationAsync = async () => {
  const response = await callAPI(`${BASE_API_URL}${API_VERSION}configuration`, API_GET, null, null, true);

  if (response.status !== 200) {
    return {};
  }

  return response.data;
}

const getRestApiDefinitionAsync = async (customerGuid) => {
  const response = await callAPI(
    `${BASE_API_URL}${API_VERSION}definition?customerGuid=${customerGuid}`,
    API_GET,
    null,
    null,
    true
  );

  if (response.status !== 200) {
    return {};
  }

  return response.data;
}

/* Organizations (customers) that have at least one integration API enabled (MQTT and/or REST).
   Superadmin-only endpoint; used to populate the org selector in the Developers/API view. */
const getApiEnabledOrganizationsAsync = async () => {
  const response = await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/api-enabled`,
    API_GET,
    null,
    null,
    true
  );

  if (response.status !== 200) {
    return [];
  }

  return response.data?.organizations ?? [];
};

// ASCII Arts from here: http://patorjk.com/software/taag/#p=display&f=Epic&t=Gateway
//  _______           _______ _________ _______  _______  _______  _______  _______
// (  ____ \|\     /|(  ____ \\__   __/(  ___  )(       )(  ____ \(  ____ )(  ____ \
// | (    \/| )   ( || (    \/   ) (   | (   ) || () () || (    \/| (    )|| (    \/
// | |      | |   | || (_____    | |   | |   | || || || || (__    | (____)|| (_____
// | |      | |   | |(_____  )   | |   | |   | || |(_)| ||  __)   |     __)(_____  )
// | |      | |   | |      ) |   | |   | |   | || |   | || (      | (\ (         ) |
// | (____/\| (___) |/\____) |   | |   | (___) || )   ( || (____/\| ) \ \__/\____) |
// (_______/(_______)\_______)   )_(   (_______)|/     \|(_______/|/   \__/\_______)
//
/* Customer is like "Lidl Italia", "Mc Donalds", etc etc */
const getCustomersDetails = async (includeDeleted = false) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "customer" +
    (includeDeleted ? "?includeDeleted=true" : "");
  return await _getWithIteration(url);
};

const getCustomersInitialState = async () => {
  const response = await getCustomersDetails();
  const customers = requireList(response?.data?.data, "customers", response);

  const customersById = {};

  customers.forEach((customer) => {
    customersById[customer.customerGuid] = {
      ...customer,
    };
  });

  return { byId: customersById, allIds: customers.map((x) => x.customerGuid) };
};

const getCustomer = async (customerGuid) => {
  return await callAPI(
      BASE_API_URL + API_VERSION + "customer/" + customerGuid ,
      API_GET
  );
};

const addCustomer = async (cName) => {
  let par = { name: cName };
  return callAPI(BASE_API_URL + API_VERSION + "customer", API_POST, par)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteCustomer = async (cGUID, hard = false) => {
  return callAPI(
    BASE_API_URL +
      API_VERSION +
      "customer/" +
      cGUID +
      (hard ? "?hardDelete=true" : ""),
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const updateCustomer = async (customerGuid, name, channel = null, channelType = null, copelandRef = null) => {
  try {
    return await callAPI(
      BASE_API_URL + API_VERSION + "customer/" + customerGuid,
      API_PUT,
      { name, channel, channelType, copelandRef }
    );
  } catch (error) {
    return error;
  }
};

const getCustomerSubscription = async (customerGUID) => {
  return await callAPI(
    BASE_API_URL + API_VERSION + "customer/" + customerGUID + "/subscription",
    API_GET,
    null,
    null,
    true
  );
};

const getCustomerMqttApi = async (customerGuid) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api`,
    API_GET,
    null,
    null,
    true
  );
};

const createCustomerMqttApiChannel = async (
  customerGuid,
  { name, notes },
  skipAlert = false,
) => {
  const body = {
    name: String(name || "").trim(),
    notes: String(notes || "").trim() || null,
  };

  return await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api/channels`,
    API_POST,
    body,
    null,
    skipAlert,
  );
};

const updateCustomerMqttApiChannel = async (
  customerGuid,
  mqttChannelGuid,
  body = {},
  skipAlert = false,
) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api/channels`,
    API_PUT,
    { ...body, channelGuid: mqttChannelGuid },
    null,
    skipAlert,
  );
};

const deleteCustomerMqttApiChannel = async (
  customerGuid,
  mqttChannelGuid,
  skipAlert = false,
) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api/channels`,
    API_DELETE,
    { channelGuid: mqttChannelGuid },
    null,
    skipAlert,
  );
};

const assignCustomerMqttApiChannelFleet = async (
  customerGuid,
  mqttChannelGuid,
  fleetGuid,
  skipAlert = false,
) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api/channels/fleet`,
    API_PUT,
    { channelGuid: mqttChannelGuid, fleetGuid },
    null,
    skipAlert,
  );
};

const unassignCustomerMqttApiChannelFleet = async (
    customerGuid,
    mqttChannelGuid,
    fleetGuid,
    skipAlert = false,
) => {
  
  return await callAPI(
      `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api/channels/fleet`,
      API_DELETE,
      { channelGuid: mqttChannelGuid, fleetGuid },
      null,
      skipAlert,
  );
};

const enableCustomerMqttApi = async (customerGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api/enable`,
    API_POST,
    null,
    null,
    skipAlert
  );
};

const disableCustomerMqttApi = async (customerGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api/disable`,
    API_POST,
    null,
    null,
    skipAlert
  );
};

const registerCustomerMqttApiCertificate = async (
  customerGuid,
  thumbprint,
  skipAlert = false
) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/mqtt-api/certificate`,
    API_PUT,
    { thumbprint },
    null,
    skipAlert
  );
};

// ─── Public API Callers ──────────────────────────────────────────────────────

const getPublicApiCallers = async (organizationGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers?organizationGuid=${organizationGuid}`,
    API_GET,
    null,
    null,
    skipAlert
  );
};

const createPublicApiCaller = async (organizationGuid, { name, contactEmail }, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers`,
    API_POST,
    { organizationGuid, name, contactEmail: contactEmail || null },
    null,
    skipAlert
  );
};

const assignFleetToPublicApiCaller = async (callerGuid, fleetGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers/${callerGuid}/fleets/${fleetGuid}`,
    API_POST,
    null,
    null,
    skipAlert
  );
};

const unassignFleetFromPublicApiCaller = async (callerGuid, fleetGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers/${callerGuid}/fleets/${fleetGuid}`,
    API_DELETE,
    null,
    null,
    skipAlert
  );
};

const deletePublicApiCaller = async (callerGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers/${callerGuid}`,
    API_DELETE,
    null,
    null,
    skipAlert
  );
};

const regeneratePublicApiCallerKey = async (callerGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers/${callerGuid}/regenerate`,
    API_POST,
    null,
    null,
    skipAlert
  );
};

const updatePublicApiCaller = async (callerGuid, { name, contactEmail }, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers/${callerGuid}`,
    API_PATCH,
    { name, contactEmail: contactEmail || null },
    null,
    skipAlert
  );
};

const suspendPublicApiCaller = async (callerGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers/${callerGuid}/suspend`,
    API_POST,
    null,
    null,
    skipAlert
  );
};

const activatePublicApiCaller = async (callerGuid, skipAlert = false) => {
  return await callAPI(
    `${BASE_API_URL}${API_VERSION}public-api/callers/${callerGuid}/activate`,
    API_POST,
    null,
    null,
    skipAlert
  );
};

// ─────────────────────────────────────────────────────────────────────────────

//  _______ __________________ _______  _______
// (  ____ \\__   __/\__   __/(  ____ \(  ____ \
// | (    \/   ) (      ) (   | (    \/| (    \/
// | (_____    | |      | |   | (__    | (_____
// (_____  )   | |      | |   |  __)   (_____  )
//       ) |   | |      | |   | (            ) |
// /\____) |___) (___   | |   | (____/\/\____) |
// \_______)\_______/   )_(   (_______/\_______)
//

const getSiteDetails = async (siteGUID) => {
  return await callAPI(BASE_API_URL + API_VERSION + "site/" + siteGUID);
};

const getSites = async (customerGUID = null, includeDeleted = false) => {
  const baseUrl = `${BASE_API_URL}${API_VERSION}site`;

  const params = new URLSearchParams();
  if (customerGUID) params.append("customerGUID", customerGUID);
  if (includeDeleted) params.append("includeDeleted", "true");

  const url = `${baseUrl}?${params.toString()}`;
  return await _getWithIteration(url, 100);
};

const addSite = async (
  sName,
  sCountry,
  sCity,
  sLatitude,
  sLongitude,
  sCustomerGUID
) => {
  let par = {
    name: sName,
    country: sCountry,
    city: sCity,
    latitude: sLatitude,
    longitude: sLongitude,
    customerGuid: sCustomerGUID,
  };
  return callAPI(BASE_API_URL + API_VERSION + "site", API_POST, par, null, true)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteSite = (sGUID, hard = false) => {
  return callAPI(
    BASE_API_URL +
      API_VERSION +
      "site/" +
      sGUID +
      (hard ? "?hardDelete=true" : ""),
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const updateSite = (
  sName,
  sCountry,
  sCity,
  sLatitude,
  sLongitude,
  sGUID,
  cGUID,
  sIsHidden,
  skipAlert = false
) => {
  let par = {
    name: sName,
    country: sCountry,
    city: sCity,
    latitude: sLatitude,
    longitude: sLongitude,
    siteGuid: sGUID,
    customerGuid: cGUID,
    isHidden: sIsHidden,
    skipAlert: skipAlert,
  };
  return callAPI(
    BASE_API_URL + API_VERSION + "site",
    API_PUT,
    par,
    null,
    skipAlert
  );
};

const getSiteLevelEmails = async (siteGUID) => {
  if (!siteGUID) return "";

  const response = await callAPI(
    BASE_API_URL + API_VERSION + "site/" + siteGUID + "/alert-recipients",
    API_GET,
    null,
    null,
    true
  ).catch((error) => {
    if (error.status === 404) return null;
    throw error;
  });

  if (!response) return { recipients: "", alertsLanguage: null };

  const { siteAlertRecipients, alertsLanguage } = response.data;
  const recipients = siteAlertRecipients.map((x) => x.email).join(";");
  return { recipients, alertsLanguage };
};

const upsertSiteLevelEmails = async (
  siteGUID,
  emails,
  alertsLanguage,
  skipAlert = true
) => {
  if (!siteGUID) return null;
  return await callAPI(
    BASE_API_URL + API_VERSION + "site/" + siteGUID + "/alert-recipients",
    API_POST,
    {
      emails:
        emails.length === 0 ? [] : utils.parseReceiverList(emails),
      alertsLanguage,
    },
    null,
    skipAlert
  );
};

const updateSiteSettings = (
  siteGuid,
  temperatureUnit,
  pressureUnit
) => {
  let par = {
    temperatureUnit: temperatureUnit,
    pressureUnit: pressureUnit
  };
  return callAPI(
    BASE_API_URL + API_VERSION + "site/" + siteGuid + "/settings",
    API_PUT,
    par
  );
};

//           _______  _______  _______  _______
// |\     /|(  ____ \(  ____ \(  ____ )(  ____ \
// | )   ( || (    \/| (    \/| (    )|| (    \/
// | |   | || (_____ | (__    | (____)|| (_____
// | |   | |(_____  )|  __)   |     __)(_____  )
// | |   | |      ) || (      | (\ (         ) |
// | (___) |/\____) || (____/\| ) \ \__/\____) |
// (_______)\_______)(_______/|/   \__/\_______)
//

const getUsersDetails = async (userGUID = null, includeDel = false) => {
  let uGUID = userGUID;
  if (uGUID === "current") {
    uGUID = window.sessionStorage.getItem("userGUID");
  }
  let url = BASE_API_URL + API_VERSION + "user";
  let first = true;
  for (const par of [
    { name: "userGuid", val: userGUID },
    { name: "includeDeleted", val: includeDel },
  ]) {
    if (par.val !== null && par.val !== false) {
      if (first === true) {
        first = false;
        url += "?" + par.name + "=" + par.val;
      } else url += "&" + par.name + "=" + par.val;
    }
  }
  if (userGUID) return await callAPI(url);
  else return await _getWithIteration(url, 50);
};

const addUsers = (userArray = []) => {
  return callAPI(BASE_API_URL + API_VERSION + "user/sub", API_PUT, userArray)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteUser = (uGUID, hard = false) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "user/" +
    uGUID +
    (hard ? "?hardDelete=true" : "");
  return callAPI(url, API_DELETE)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

//           _______  _______  _______    _______  _______  _        _______
// |\     /|(  ____ \(  ____ \(  ____ )  (  ____ )(  ___  )( \      (  ____ \
// | )   ( || (    \/| (    \/| (    )|  | (    )|| (   ) || (      | (    \/
// | |   | || (_____ | (__    | (____)|  | (____)|| |   | || |      | (__
// | |   | |(_____  )|  __)   |     __)  |     __)| |   | || |      |  __)
// | |   | |      ) || (      | (\ (     | (\ (   | |   | || |      | (
// | (___) |/\____) || (____/\| ) \ \__  | ) \ \__| (___) || (____/\| (____/\
// (_______)\_______)(_______/|/   \__/  |/   \__/(_______)(_______/(_______/
//
const getUserRole = async (userGUID) => {
  return await callAPI(BASE_API_URL + API_VERSION + "user-role/" + userGUID);
};

const deleteUserRole = (userGUID) => {
  return callAPI(
    BASE_API_URL + API_VERSION + "user-role/" + userGUID,
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const assignRoleToUser = (userGUID, userRole) => {
  let par = {
    userGuid: userGUID,
    role: userRole,
  };
  return callAPI(BASE_API_URL + API_VERSION + "user-role", API_POST, par)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const updateUserRole = (userGUID, userRole) => {
  let par = {
    userGuid: userGUID,
    role: userRole,
  };
  return callAPI(BASE_API_URL + API_VERSION + "user-role", API_PUT, par)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

//  _______  _______ _________ _______           _______
// (  ____ \(  ___  )\__   __/(  ____ \|\     /|(  ___  )|\     /|
// | (    \/| (   ) |   ) (   | (    \/| )   ( || (   ) |( \   / )
// | |      | (___) |   | |   | (__    | | _ | || (___) | \ (_) /
// | | ____ |  ___  |   | |   |  __)   | |( )| ||  ___  |  \   /
// | | \_  )| (   ) |   | |   | (      | || || || (   ) |   ) (
// | (___) || )   ( |   | |   | (____/\| () () || )   ( |   | |
// (_______)|/     \|   )_(   (_______/(_______)|/     \|   \_/
//
const getGatewayByMAC = async (mac) => {
  let url = BASE_API_URL + API_VERSION + "gateway/?macAddress=" + mac;
  return await callAPI(url);
};

// Registering a gateway cannot start without this answer, so a failure that
// looks transient (no response, 5xx, 408, 429, a 401 mid token renewal) is
// asked again, like a page of a listing. A 404 (Gateway.NotFound) is not.
const getGatewayBrief = async (id) => {
  const url = BASE_API_URL + API_VERSION + "gateway/" + id + "/brief";
  return await retryTransient(async () => {
    // Read per attempt: a token renewed in between is picked up.
    const config = await getAPICallAuthHeader();
    if (!isAuthReady(config)) return null;
    config.headers["X-Skip-Not-Found-Error-Log"] = "true";
    return await callAPI(url, API_GET, null, config, true);
  }, `gateway brief ${id}`);
};

const getGatewayDetails = async (siteGUID = null) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "gateway" +
    (siteGUID !== null ? "/?siteGuid=" + siteGUID : "");
  if (siteGUID) return await callAPI(url);
  else return await _getWithIteration(url, 50);
};

const getGatewaysFullDetails = async (siteGUID = null) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "gateway/details" +
    (siteGUID !== null ? "/?siteGuid=" + siteGUID : "");
  if (siteGUID) return await callAPI(url);
  else return await _getWithIteration(url, 100);
};

const getGatewaysFullDetailsByCustomer = async (customerGuid) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "gateway/details/preview" +
    "?customerGuid=" +
    customerGuid;
  return await callAPI(url);
};

const getSingleGatewayDetail = async (gatewayGuid = null) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "gateway" +
    (gatewayGuid !== null ? "/" + gatewayGuid : "");

  if (gatewayGuid) return await callAPI(url);
  else return await _getWithIteration(url, 50);
};

const addGateway = (gwinfo) => {
  return callAPI(BASE_API_URL + API_VERSION + "gateway", API_POST, gwinfo)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteGateway = (gwGUID, hard = false) => {
  return callAPI(
    BASE_API_URL +
      API_VERSION +
      "gateway/" +
      gwGUID +
      (hard ? "?hardDelete=true" : ""),
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const updateGateway = (
  gwGUID,
  gwName,
  gwTelemetryInterval,
  sGUID,
  xigVisibility = null,
  disableAutomaticDetection = null,
  interframeDelay = null
) => {
  let par = {
    gatewayGuid: gwGUID,
    siteGuid: sGUID,
    name: gwName,
    telemetryPushInterval: gwTelemetryInterval,
    xigVisibility: xigVisibility,
    disableAutomaticDetection: disableAutomaticDetection,
    interframeDelay: interframeDelay,
  };
  return callAPI(BASE_API_URL + API_VERSION + "gateway", API_PUT, par)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const refreshVars = async (gatewayGuid = null) => {
  if (gatewayGuid !== null && gatewayGuid !== undefined) {
    let gateway = (await getSingleGatewayDetail(gatewayGuid)).data;

    if (gateway.status === "ON") {
      await sendMessageToGateway(gatewayGuid, {
        type: "gcmd",
        cmd: "refreshvars",
      });
    }
  }
};

const changeGatewaySite = async (gatewayGuid, newSiteGuid) => {
  let par = {
    gatewayGuid: gatewayGuid,
    destinationSiteGuid: newSiteGuid,
  };
  return await callAPI(
    BASE_API_URL + API_VERSION + "gateway/change-site",
    API_POST,
    par,
    null,
    true
  );
};

// Only Admin!
const getGatewaysFull = async (includeDeleted = true) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "gateway/full?includeDeleted=" +
    includeDeleted;

  return await _getWithIteration(url);
};

const wipeGatewayConfigAsync = async (gatewayGuid) => {
  const url = `${BASE_API_URL}${API_VERSION}gateway/${gatewayGuid}/wipe-configuration`;
  return await callAPI(url, API_POST, null, null, true);
};

//  _______  _______  _______  _______  _______  _______ _________ _______  _______
// (  ____ )(  ___  )(  ____ )(  ___  )(       )(  ____ \\__   __/(  ____ \(  ____ )
// | (    )|| (   ) || (    )|| (   ) || () () || (    \/   ) (   | (    \/| (    )|
// | (____)|| (___) || (____)|| (___) || || || || (__       | |   | (__    | (____)|
// |  _____)|  ___  ||     __)|  ___  || |(_)| ||  __)      | |   |  __)   |     __)
// | (      | (   ) || (\ (   | (   ) || |   | || (         | |   | (      | (\ (
// | )      | )   ( || ) \ \__| )   ( || )   ( || (____/\   | |   | (____/\| ) \ \__
// |/       |/     \||/   \__/|/     \||/     \|(_______/   )_(   (_______/|/   \__/

const updateParameterMap = async (gatewayGuid = null, addresses = []) => {
  if (gatewayGuid !== null && gatewayGuid !== undefined) {
    let gateway = (await getSingleGatewayDetail(gatewayGuid)).data;

    let sAddr = [];

    for (let adr of addresses) {
      sAddr.push(adr.toString());
    }

    if (gateway.status === "ON") {
      await sendMessageToGateway(
        gatewayGuid,
        {
          type: "gcmd",
          cmd: "refresh_params",
          dvc: sAddr,
        },
        false
      );
    }
  }
};

const downloadMap = async (
  macAddress = null,
  device = null,
  changes = [],
  ftype = "bin"
) => {
  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    responseType: "blob",
  };

  return (
    await callAPI(
      BASE_API_URL +
        API_VERSION +
        "device-map/download?device=" +
        device +
        "&macAddress=" +
        macAddress +
        "&ftype=" +
        ftype,
      API_POST,
      createUpdateParFile(changes),
      config
    )
  ).data;
};

const uploadMap = async (deviceGuid = null, fileMap = null) => {
  if (!deviceGuid || !fileMap) return null;

  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    headers: { ...config.headers, "Content-Type": "multipart/form-data" },
  };

  let url =
    BASE_API_URL + API_VERSION + "device-map/upload?deviceGuid=" + deviceGuid;

  return callAPIWithCancel(url, API_POST, fileMap, config);
};

export const WX_FUNCTIONS = {
  READ: API_GET,
  EDIT: API_PUT,
  WRITE: API_POST,
};

const parameterMap = async (
  deviceGuid = null,
  func,
  parList = null,
  writeAll = false,
  resetNeeded = 1
) => {
  if (deviceGuid !== null) {
    let config = null;

    if (Object.values(WX_FUNCTIONS).indexOf(func) === -1) {
      config = await getAPICallAuthHeader();
      if (!isAuthReady(config)) return null;
      config = {
        ...config,
        responseType: "blob",
      };
    }

    return await callAPIWithCancel(
      BASE_API_URL +
        API_VERSION +
        "device-map" +
        "?deviceGuid=" +
        deviceGuid +
        (func === WX_FUNCTIONS.WRITE
          ? "&resetNeeded=" + resetNeeded + "&writeAll=" + writeAll
          : ""),
      func,
      func === WX_FUNCTIONS.EDIT || func === WX_FUNCTIONS.WRITE
        ? createUpdateParFile(parList)
        : null,
      config,
      true
    );
  }
};

const createUpdateParFile = (parList) => {
  let updatePar = [];

  for (let par of parList) {
    updatePar.push({
      label: par.l,
      value: par.v,
      visibility: par.vis,
      editability: par.edi,
    });
  }

  return updatePar;
};

/* const writeParameters = async (gatewayGuid = null, address, parList) => {
  if (gatewayGuid !== null && gatewayGuid !== undefined && parList.length > 0) {
    let gateway = (await getSingleGatewayDetail(gatewayGuid)).data;
    if (gateway.status === "ON") {
      let updatePar = createUpdateParFile(parList);

      await sendMessageToGateway(gatewayGuid, {
        type: "gcmd",
        cmd: "write_params",
        dvc: [{ id: address, pars: updatePar }],
      });
    }
  }
}; */

const exportParametersList = async (deviceGuids = [], type = "pdf") => {
  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    responseType: "blob",
  };

  let url = BASE_API_URL + API_VERSION + "device-map/export?";

  for (let i = 0; i < deviceGuids.length; i++) {
    url += i > 0 ? "&" : "";
    url += "deviceGuids=" + deviceGuids[i];
  }

  url += "&ftype=" + type;

  return (await callAPI(url, API_POST, null, config)).data;
};

//  ______   _______          _________ _______  _______  _______
// (  __  \ (  ____ \|\     /|\__   __/(  ____ \(  ____ \(  ____ \
// | (  \  )| (    \/| )   ( |   ) (   | (    \/| (    \/| (    \/
// | |   ) || (__    | |   | |   | |   | |      | (__    | (_____
// | |   | ||  __)   ( (   ) )   | |   | |      |  __)   (_____  )
// | |   ) || (       \ \_/ /    | |   | |      | (            ) |
// | (__/  )| (____/\  \   /  ___) (___| (____/\| (____/\/\____) |
// (______/ (_______/   \_/   \_______/(_______/(_______/\_______)
//

const getDevicesDetails = async (gatewayGuid = null) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "device" +
    (gatewayGuid !== null ? "/?gatewayGuid=" + gatewayGuid : "");

  if (gatewayGuid) return await callAPI(url);
  else return await _getWithIteration(url);
};

// `activeOrganizationGuid`: the organization the user is working in. IoT.API answers
// canViewTemplates / canManageTemplates for it (the device's own when left out).
const getDevice = async (deviceGuid = null, activeOrganizationGuid = null) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "device" +
    (deviceGuid !== null ? "/" + deviceGuid : "") +
    (deviceGuid !== null && activeOrganizationGuid
      ? `?activeOrganization=${encodeURIComponent(activeOrganizationGuid)}`
      : "");
  if (deviceGuid) return await callAPI(url);
  else return await _getWithIteration(url);
};

// Only Admin!
const getDevicesFull = async (includeDeleted = true) => {
  let url =
    BASE_API_URL + API_VERSION + "device/full?includeDeleted=" + includeDeleted;

  return await _getWithIteration(url);
};

// The gateway's own device (dongle) carries WiFi as the `rssiLevel` envelope
// metric, which is not a configured point and so is absent from GET /device.
// Seed the keys (no extra call) so the existing latest-measures flow (connected
// devices page) and the SignalR reducer can fill them.
const seedDongleWifiPoints = (dvcData, dvc) => {
  if (dvcData.isDongleController || dvc.isDongleController) {
    dvcData.pointsById = seedWifiPoints(dvcData.pointsById);
  }
};

// One device of a listing, in the store's shape (`pointsById`), or null when
// it can't be read: one bad device never fails the whole listing.
const loadListedDevice = async (dvc, activeOrganizationGuid) => {
  try {
    let dvcData = (await getDevice(dvc.deviceGuid, activeOrganizationGuid)).data;

    if (dvcData.points === null) {
      dvcData.points = [];
    }

    for (let i = 0; i < dvcData.points.length; i++) {
      if (dvcData.points[i].lastValue === null)
        dvcData.points[i].lastValue = undefined;
    }

    dvcData.points = dvcData.points.filter((data) => {
      return data.hidden !== true;
    });

    let pointByIdObj = [];
    for (let point of dvcData.points) {
      pointByIdObj = {
        ...pointByIdObj,
        [point.code]: point,
      };
    }
    delete dvcData.points;
    dvcData["pointsById"] = pointByIdObj;

    seedDongleWifiPoints(dvcData, dvc);

    return dvcData;
  } catch (ex) {
    utils.debug(
      "Invalid dvc or json files for this device: " + dvc.deviceGuid,
      utils.MSG_TYPE_WARN
    );
    return null;
  }
};

// The devices of `gateways`, all asked for at once, in the listing's order,
// as the controllers store shape.
const loadListedDevices = async (gateways, activeOrganizationGuid) => {
  const listed = gateways.flatMap((gw) => gw.devices);
  const devices = (
    await Promise.all(listed.map((dvc) => loadListedDevice(dvc, activeOrganizationGuid)))
  ).filter(Boolean);

  var controllers = { byId: {}, allIds: [] };
  for (var c of devices) {
    controllers = {
      ...controllers,
      byId: { ...controllers.byId, [c.deviceGuid]: c },
    };
    controllers.allIds.push(c.deviceGuid);
  }
  return controllers;
};

// `activeOrganizationGuid`: passed on to getDevice, for the template fields.
const getDevicesOnGateway = async (gwGUID, activeOrganizationGuid = null) => {
  const response = await getGatewayDetails();
  const gateways = requireList(response?.data?.data, "gateways", response);
  const totalGateways = response.data.total;

  if (totalGateways === 0) return { byId: {}, allIds: [] };
  return loadListedDevices(
    gateways.filter((gw) => gw.gatewayGuid === gwGUID),
    activeOrganizationGuid
  );
};

// `activeOrganizationGuid`: passed on to getDevice, for the template fields.
const getDevicesOnSite = async (sGUID, activeOrganizationGuid = null) => {
  const pl = (await getGatewayDetails(sGUID)).data;
  const gateways = pl.data;
  const totalGateways = pl.total;

  if (totalGateways === 0) return { byId: {}, allIds: [] };
  return loadListedDevices(
    gateways.filter((gw) => gw.site.siteGuid === sGUID),
    activeOrganizationGuid
  );
};

const updateDevice = (dGuid, name, serialID) => {
  let param = {
    deviceGuid: dGuid,
    friendlyName: name,
    cabinetSerial: serialID,
  };

  return callAPI(BASE_API_URL + API_VERSION + "device", API_PUT, param)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const updateDeviceCustomizations = async (
  deviceGuid,
  customizationData = {}
) => {
  const url = `${BASE_API_URL}${API_VERSION}device/${deviceGuid}/customizations`;
  return await callAPI(url, API_PUT, customizationData);
};

//  _______  _______  _______  _______  _______  _______  _______ _________ _______  ______          _______  _______  _______  _______           _______  _______  _______
// (  ___  )(  ____ \(  ____ \(  ____ )(  ____ \(  ____ \(  ___  )\__   __/(  ____ \(  __  \        (       )(  ____ \(  ___  )(  ____ \|\     /|(  ____ )(  ____ \(  ____ \
// | (   ) || (    \/| (    \/| (    )|| (    \/| (    \/| (   ) |   ) (   | (    \/| (  \  )       | () () || (    \/| (   ) || (    \/| )   ( || (    )|| (    \/| (    \/
// | (___) || |      | |      | (____)|| (__    | |      | (___) |   | |   | (__    | |   ) | _____ | || || || (__    | (___) || (_____ | |   | || (____)|| (__    | (_____
// |  ___  || | ____ | | ____ |     __)|  __)   | | ____ |  ___  |   | |   |  __)   | |   | |(_____)| |(_)| ||  __)   |  ___  |(_____  )| |   | ||     __)|  __)   (_____  )
// | (   ) || | \_  )| | \_  )| (\ (   | (      | | \_  )| (   ) |   | |   | (      | |   ) |       | |   | || (      | (   ) |      ) || |   | || (\ (   | (            ) |
// | )   ( || (___) || (___) || ) \ \__| (____/\| (___) || )   ( |   | |   | (____/\| (__/  )       | )   ( || (____/\| )   ( |/\____) || (___) || ) \ \__| (____/\/\____) |
// |/     \|(_______)(_______)|/   \__/(_______/(_______)|/     \|   )_(   (_______/(______/        |/     \|(_______/|/     \|\_______)(_______)|/   \__/(_______/\_______)

const getDeviceMeasures = async (
  deviceGuid = "",
  dateFrom = null,
  dateTo = new Date(),
  aggregateMeasures = true
) => {
  let strTo;
  let strFrom;

  if (dateFrom === null) {
    strTo = utils.formatDate(dateTo).replace(/:/g, "%3A");
    strFrom = utils
      .formatDate(utils.setOffsetDate(dateTo, minutes))
      .replace(/:/g, "%3A");
  } else {
    strFrom = dateFrom;
    strTo = dateTo;
  }

  let url =
    BASE_API_URL +
    API_VERSION +
    "device/measures?" +
    "deviceGuid=" +
    deviceGuid +
    "&from=" +
    strFrom +
    "&to=" +
    strTo +
    "&aggregateMeasures=" +
    aggregateMeasures;

  return await callAPIWithCancel(url);
};

const getLatestValidMeasure = async (deviceGuid) => {
  const url =
    BASE_API_URL +
    API_VERSION +
    "device/latest-measures?deviceGuid=" +
    deviceGuid +
    "&useLatestValid=true";
  return callAPI(url, API_GET, null, null, true);
};

//           _______  _     _________ _______  _______  _        _______  _        _______ __________________ _______  _               _______ _________ _        _______  _______
// |\     /|(  ____ \( \    \__   __/(  ____ )(  ___  )( (    /|(  ____ \( \      (  ___  )\__   __/\__   __/(  ___  )( (    /|       (  ____ \\__   __/( \      (  ____ \(  ____ \
// | )   ( || (    \/| (       ) (   | (    )|| (   ) ||  \  ( || (    \/| (      | (   ) |   ) (      ) (   | (   ) ||  \  ( |       | (    \/   ) (   | (      | (    \/| (    \/
// | | _ | || (__    | | _____ | |   | (____)|| (___) ||   \ | || (_____ | |      | (___) |   | |      | |   | |   | ||   \ | | _____ | (__       | |   | |      | (__    | (_____
// | |( )| ||  __)   | |(_____)| |   |     __)|  ___  || (\ \) |(_____  )| |      |  ___  |   | |      | |   | |   | || (\ \) |(_____)|  __)      | |   | |      |  __)   (_____  )
// | || || || (      | |       | |   | (\ (   | (   ) || | \   |      ) || |      | (   ) |   | |      | |   | |   | || | \   |       | (         | |   | |      | (            ) |
// | () () || (____/\| (____/\ | |   | ) \ \__| )   ( || )  \  |/\____) || (____/\| )   ( |   | |   ___) (___| (___) || )  \  |       | )      ___) (___| (____/\| (____/\/\____) |
// (_______)(_______/(_______/ )_(   |/   \__/|/     \||/    )_)\_______)(_______/|/     \|   )_(   \_______/(_______)|/    )_)       |/       \_______/(_______/(_______/\_______)
//

const getDeviceWelTranslationFiles = async (
  dpdGuid = null,
  mfve = null,
  limit = 50
) => {
  let url =
    BASE_API_URL + API_VERSION + "device-parameter-description-translations";
  let first = true;
  for (const par of [
    { name: "deviceParametersDescriptionGuid", val: dpdGuid },
    { name: "model", val: mfve },
  ]) {
    if (par.val !== null) {
      if (first === true) {
        first = false;
        url += "?" + par.name + "=" + par.val;
      } else url += "&" + par.name + "=" + par.val;
    }
  }

  if (dpdGuid) return await callAPI(url);
  else return await _getWithIteration(url, limit);
};

const getDeviceWelTranslationFileDetails = async (dpdGuid) => {
  return await callAPI(
    BASE_API_URL +
      API_VERSION +
      "device-parameter-description-translations/" +
      dpdGuid
  );
};

const addUpdateDeviceWelTranslationFile = async (mfve, file) => {
  const existingWelTFile = (await getDeviceWelTranslationFiles(null, mfve))
    .data;

  const model = utils.getModelFromMFVE(mfve);
  const fve_split = mfve.split("_")[1].split("-");

  const existingWelFile = (
    await getDeviceWelFiles(model, fve_split[0], fve_split[1], fve_split[2])
  ).data;

  if (existingWelFile.total === 0) return null;

  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    header: { ...config.header, "content-type": "multipart/form-data" },
  };
  let data = new FormData();
  data.append("formFile", file);
  if (existingWelTFile.total > 0) {
    // Update existing
    return callAPI(
      BASE_API_URL +
        API_VERSION +
        "device-parameter-description-translations?deviceParametersDescriptionTranslationsGuid=" +
        existingWelTFile.data[0].deviceParametersDescriptionTranslationsGuid +
        "&culture=en-GB",
      API_PUT,
      data,
      config
    )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  } else {
    // Create new
    return callAPI(
      BASE_API_URL +
        API_VERSION +
        "device-parameter-description-translations?deviceParametersDescriptionGuid=" +
        existingWelFile.data[0].deviceParametersDescriptionGuid +
        "&culture=en-GB",
      API_POST,
      data,
      config
    )
      .then((response) => {
        return response;
      })
      .catch((error) => {
        return error;
      });
  }
};

const deleteDeviceWelTranslationFile = (dpdGuid, hard = false) => {
  return callAPI(
    BASE_API_URL +
      API_VERSION +
      "device-parameter-description-translations/" +
      dpdGuid +
      (hard ? "?hardDelete=true" : ""),
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};
//           _______  _         _______ _________ _        _______  _______
// |\     /|(  ____ \( \       (  ____ \\__   __/( \      (  ____ \(  ____ \
// | )   ( || (    \/| (       | (    \/   ) (   | (      | (    \/| (    \/
// | | _ | || (__    | | _____ | (__       | |   | |      | (__    | (_____
// | |( )| ||  __)   | |(_____)|  __)      | |   | |      |  __)   (_____  )
// | || || || (      | |       | (         | |   | |      | (            ) |
// | () () || (____/\| (____/\ | )      ___) (___| (____/\| (____/\/\____) |
// (_______)(_______/(_______/ |/       \_______/(_______/(_______/\_______)
//

const getDeviceWelFiles = async (
  model = null,
  family = null,
  version = null,
  memory = null,
  limit = 50
) => {
  let opt = "";
  let first = true;
  for (const par of [
    { name: "model", val: model },
    { name: "family", val: family },
    { name: "version", val: version },
    { name: "memory", val: memory },
  ]) {
    if (par.val !== null) {
      if (first === true) {
        first = false;
        opt += "?" + par.name + "=" + par.val;
      } else opt += "&" + par.name + "=" + par.val;
    }
  }

  return await _getWithIteration(
    BASE_API_URL + API_VERSION + "device-parameter-description" + opt,
    limit
  );
};

const getDeviceWelFileDetails = async (dpdGuid) => {
  return await callAPI(
    BASE_API_URL + API_VERSION + "device-parameter-description/" + dpdGuid
  );
};

const addUpdateDeviceWelFile = async (mfve, file) => {
  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    header: { ...config.header, "content-type": "multipart/form-data" },
  };
  let data = new FormData();
  data.append("formFile", file);
  return callAPI(
    BASE_API_URL + API_VERSION + "device-parameter-description?model=" + mfve,
    API_POST,
    data,
    config
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteDeviceWelFile = (dpdGuid, hard = false) => {
  return callAPI(
    BASE_API_URL +
      API_VERSION +
      "device-parameter-description/" +
      dpdGuid +
      (hard ? "?hardDelete=true" : ""),
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

//  _______  _______ _________ _______           _______                _______ _________ _        _______  _______
// (  ____ \(  ___  )\__   __/(  ____ \|\     /|(  ___  )|\     /|     (  ____ \\__   __/( \      (  ____ \(  ____ \
// | (    \/| (   ) |   ) (   | (    \/| )   ( || (   ) |( \   / )     | (    \/   ) (   | (      | (    \/| (    \/
// | |      | (___) |   | |   | (__    | | _ | || (___) | \ (_) /_____ | (__       | |   | |      | (__    | (_____
// | | ____ |  ___  |   | |   |  __)   | |( )| ||  ___  |  \   /(_____)|  __)      | |   | |      |  __)   (_____  )
// | | \_  )| (   ) |   | |   | (      | || || || (   ) |   ) (        | (         | |   | |      | (            ) |
// | (___) || )   ( |   | |   | (____/\| () () || )   ( |   | |        | )      ___) (___| (____/\| (____/\/\____) |
// (_______)|/     \|   )_(   (_______/(_______)|/     \|   \_/        |/       \_______/(_______/(_______/\_______)
//

const getDeviceRuntimeCommandFiles = async (
  model = null,
  family = null,
  version = null,
  memory = null,
  limit = 50
) => {
  let first = true;
  let url = BASE_API_URL + API_VERSION + "device-runtime-commands";
  for (const par of [
    { name: "model", val: model },
    { name: "family", val: family },
    { name: "version", val: version },
    { name: "memory", val: memory },
  ]) {
    if (par.val !== null) {
      if (first === true) {
        first = false;
        url += "?" + par.name + "=" + par.val;
      } else url += "&" + par.name + "=" + par.val;
    }
  }
  return await _getWithIteration(url, limit);
};

const getDeviceRuntimeCommandFileDetails = async (drcGuid) => {
  return await callAPI(
    BASE_API_URL + API_VERSION + "device-runtime-commands/" + drcGuid
  );
};

const addUpdateDeviceRuntimeCommandFile = async (mfve, file) => {
  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    header: { ...config.header, "content-type": "multipart/form-data" },
  };
  let data = new FormData();
  data.append("formFile", file);
  return callAPI(
    BASE_API_URL + API_VERSION + "device-runtime-commands?model=" + mfve,
    API_POST,
    data,
    config
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteDeviceRuntimeCommandFile = (drcGuid, hard = false) => {
  return callAPI(
    BASE_API_URL +
      API_VERSION +
      "device-runtime-commands/" +
      drcGuid +
      (hard ? "?hardDelete=true" : ""),
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

//  ______   _______          _________ _______  _______  _________          _______  _______      _______ _________ _        _______  _______
// (  __  \ (  ____ \|\     /|\__   __/(  ____ \(  ____ \ \__   __/|\     /|(  ____ )(  ____ \    (  ____ \\__   __/( \      (  ____ \(  ____ \
// | (  \  )| (    \/| )   ( |   ) (   | (    \/| (    \/    ) (   ( \   / )| (    )|| (    \/    | (    \/   ) (   | (      | (    \/| (    \/
// | |   ) || (__    | |   | |   | |   | |      | (__  _____ | |    \ (_) / | (____)|| (__  _____ | (__       | |   | |      | (__    | (_____
// | |   | ||  __)   ( (   ) )   | |   | |      |  __)(_____)| |     \   /  |  _____)|  __)(_____)|  __)      | |   | |      |  __)   (_____  )
// | |   ) || (       \ \_/ /    | |   | |      | (          | |      ) (   | (      | (          | (         | |   | |      | (            ) |
// | (__/  )| (____/\  \   /  ___) (___| (____/\| (____/\    | |      | |   | )      | (____/\    | )      ___) (___| (____/\| (____/\/\____) |
// (______/ (_______/   \_/   \_______/(_______/(_______/    )_(      \_/   |/       (_______/    |/       \_______/(_______/(_______/\_______)
//

const getDeviceTypeFiles = async (limit = 50) => {
  return await _getWithIteration(
    BASE_API_URL + API_VERSION + "device-type-configuration",
    limit
  );
};

const getDeviceTypeFileDetails = async (dtcGuid) => {
  return await callAPI(
    BASE_API_URL + API_VERSION + "device-type-configuration/" + dtcGuid
  );
};

const addUpdateDeviceTypeFile = async (mfve, file) => {
  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    header: { ...config.header, "content-type": "multipart/form-data" },
  };
  let data = new FormData();
  data.append("formFile", file);
  return callAPI(
    BASE_API_URL + API_VERSION + "device-type-configuration?model=" + mfve,
    API_POST,
    data,
    config
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteDeviceTypeFile = (dtcGuid, hard = false) => {
  return callAPI(
    BASE_API_URL +
      API_VERSION +
      "device-type-configuration/" +
      dtcGuid +
      (hard ? "?hardDelete=true" : ""),
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const getDeviceDSCList = async () => {
  const [
    typeConfigurationsPromiseResult,
    runtimeCommandsPromiseResult,
    parametersDescriptionsPromiseResult,
    parametersDescriptionTranslationsPromiseResult,
  ] = await Promise.all([
    getDeviceTypeFiles(9000),
    getDeviceRuntimeCommandFiles(null, null, null, null, 9000),
    getDeviceWelFiles(null, null, null, null, 9000),
    getDeviceWelTranslationFiles(null, null, 9000),
  ]);

  const listOf = (response, listing) =>
    requireList(response?.data?.data, listing, response);
  const typeConfigurations = listOf(
    typeConfigurationsPromiseResult,
    "device type files"
  );
  const runtimeCommands = listOf(
    runtimeCommandsPromiseResult,
    "runtime command files"
  );
  const parametersDescriptions = listOf(
    parametersDescriptionsPromiseResult,
    "parameter description files"
  );
  const parametersDescriptionTranslations = listOf(
    parametersDescriptionTranslationsPromiseResult,
    "parameter description translation files"
  );

  let dscFilesList = [];
  for (const deviceTypeConfiguration of typeConfigurations) {
    const [model, fve] = deviceTypeConfiguration.model.split("_");
    const [family, version, eeprom] = fve ? fve.split("-") : [null, null, null];

    const deviceRuntimeCommands = runtimeCommands.find(
      (x) => x.model === deviceTypeConfiguration?.model
    );
    const deviceParametersDescription = parametersDescriptions.find(
      (x) => x.model === deviceTypeConfiguration?.model
    );
    const deviceParametersDescriptionTranslations =
      parametersDescriptionTranslations.find(
        (x) =>
          x.deviceParametersDescriptionGuid ===
          deviceParametersDescription?.deviceParametersDescriptionGuid
      );

    dscFilesList.push({
      guid: deviceTypeConfiguration.deviceTypeConfigurationGuid,
      model: model,
      family: family,
      version: version,
      e2: eeprom,
      wel: {
        state:
          deviceParametersDescription &&
          deviceParametersDescriptionTranslations,
        wGuid: deviceParametersDescription
          ? deviceParametersDescription.deviceParametersDescriptionGuid
          : null,
        tGuid: deviceParametersDescriptionTranslations
          ? deviceParametersDescriptionTranslations.deviceParametersDescriptionTranslationsGuid
          : null,
      },
      gwdsc: {
        state: !!deviceRuntimeCommands,
        guid: deviceRuntimeCommands
          ? deviceRuntimeCommands.deviceRuntimeCommandsGuid
          : null,
      },
    });
  }
  return dscFilesList;
};

const getGatewayInitialState = async (
  customerGuid = null,
  siteGuid = null,
  gatewaysList = null,
  fromDashboard = false
) => {
  let gateways = { byId: {}, allIds: [] };

  let list = gatewaysList;
  if (!list) {
    // The organization endpoint answers a bare array, the full one a page.
    const response = customerGuid
      ? await getGatewaysFullDetailsByCustomer(customerGuid)
      : await getGatewaysFullDetails(siteGuid);
    list = requireList(
      customerGuid ? response?.data : response?.data?.data,
      "gateways",
      response
    );
  }
  let apigateways = utils.sortArrayOfObjects(list, "name");

  for (var g of apigateways) {
    g.currentDateTime = new Date();

    gateways = {
      ...gateways,
      byId: { ...gateways.byId, [g.gatewayGuid]: g },
    };
    gateways.allIds.push(g.gatewayGuid);
  }

  if (fromDashboard) {
    gateways = { ...gateways, fromDashboard: true };
  }
  return gateways;
};

// SITES
const getSiteInitialState = async (
  siteList = null,
  fromDashboard = false,
  customerGuid = null
) => {
  let list = siteList;
  if (!list) {
    const response = await getSites(customerGuid);
    list = requireList(response?.data?.data, "sites", response);
  }
  let apisites = utils.sortArrayOfObjects(list, "name");
  var sites = { byId: {}, allIds: [] };

  // These statements will be removed
  for (var site of apisites) {
    if (!site.connectivitytype) site.connectivitytype = "wifi";
    if (!site.latitude) site.latitude = 41.9109;
    if (!site.longitude) site.longitude = 12.4818;
    sites = {
      ...sites,
      byId: { ...sites.byId, [site.siteGuid]: site },
    };
    sites.allIds.push(site.siteGuid);
  }

  if (fromDashboard) {
    sites = { ...sites, fromDashboard: true };
  }

  return sites;
};

const sendMessageToGateway = async (guid, message, skipAlert = true) => {
  try {
    return await callAPI(
      BASE_API_URL + API_VERSION + "gateway/send-message",
      API_POST,
      { guid, message },
      null,
      skipAlert
    );
  } catch (error) {
    return error;
  }
};

// **********************************************************
const getAllThresholdDetails = async (
  deviceGuid = null,
  gatewayGuid = null,
  customerGuid = null
) => {
  let url = BASE_API_URL + API_VERSION + "threshold";
  let first = true;
  for (const par of [
    { name: "deviceGuid", val: deviceGuid },
    { name: "gatewayGuid", val: gatewayGuid },
    { name: "customerGuid", val: customerGuid },
  ]) {
    if (par.val !== null) {
      if (first === true) {
        first = false;
        url += "?" + par.name + "=" + par.val;
      } else url += "&" + par.name + "=" + par.val;
    }
  }
  return await _getWithIteration(url, 100);
};

/* 
  body = {
    deviceGuid,
    label,
    fieldName,
    minValue,
    maxValue,
    minHysteresis,
    maxHysteresis,
    alertDelay
  }
*/

const createNewThresholds = async (body) => {
  try {
    return await callAPI(BASE_API_URL + API_VERSION + "threshold", API_POST, body);
  } catch (error) {
    return error;
  }
};

const setDefaultThresholds = async (deviceGuid, receivers) => {
  try {
    return await callAPI(
      BASE_API_URL + API_VERSION + `device/${deviceGuid}/default-thresholds`,
      API_POST,
      { receivers }
    );
  } catch (error) {
    return error;
  }
};

/* 
  body = {
    deviceGuid,
    label,
    fieldName,
    minValue,
    maxValue,
    minHysteresis,
    maxHysteresis,
    alertDelay
  }
*/
const editExistingThreshold = async (body) => {
  try {
    return await callAPI(BASE_API_URL + API_VERSION + "threshold", API_PUT, body);
  } catch (error) {
    return error;
  }
};

const getAllThresholdDetailsFull = async (
  deviceGuid = null,
  gatewayGuid = null,
  includeDeleted = false
) => {
  let url = BASE_API_URL + API_VERSION + "threshold/full";
  let first = true;
  for (const par of [
    { name: "deviceGuid", val: deviceGuid },
    { name: "gatewayGuid", val: gatewayGuid },
    { name: "includeDeleted", val: includeDeleted ? "true" : "false" },
  ]) {
    if (par.val !== null) {
      if (first === true) {
        first = false;
        url += "?" + par.name + "=" + par.val;
      } else url += "&" + par.name + "=" + par.val;
    }
  }
  return await _getWithIteration(url, 100);
};

const getThresholdDetailsByGuid = async (thresholdGuid) => {
  return await callAPI(
    BASE_API_URL + API_VERSION + "threshold/" + thresholdGuid
  );
};

const deleteThreshold = async (thresholdGuid, hard = true) => {
  // Remove alert from redux store if active
  reduxStore.dispatch(removeAlert(thresholdGuid));

  try {
    return await callAPI(
      BASE_API_URL +
      API_VERSION +
      "threshold/" +
      thresholdGuid +
      (hard ? "?hardDelete=true" : ""),
      API_DELETE
    );
  } catch (error) {
    return error;
  }
};

// #################################################
const getAllThresholdNotify = async (thresholdGuid = null) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "threshold-email-notify" +
    (thresholdGuid !== null ? "?thresholdGuid=" + thresholdGuid : "");
  if (thresholdGuid) return await callAPI(url);
  else return await _getWithIteration(url, 100);
};

// arr is an array of objects like the following:
// enableNotification: "ON" or "OFF",
// email: p.email,
// thresholdGuid: p.thresholdGuid,
// organizationId: p.organizationId,
// siteId: p.siteId,
const createOrUpdateThresholdNotify = async (arr) => {
  return callAPI(
    BASE_API_URL + API_VERSION + "threshold-email-notify/list",
    API_POST,
    arr
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteThresholdNotify = (thresholdGuid, email) => {
  return callAPI(
    BASE_API_URL +
      API_VERSION +
      "threshold-email-notify?thresholdGuid=" +
      thresholdGuid +
      "&email=" +
      email,
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const deleteAllThresholdNotifyForThreshold = (thresholdGuid) => {
  return callAPI(
    BASE_API_URL + API_VERSION + "threshold-email-notify/" + thresholdGuid,
    API_DELETE
  )
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

//  _______  _        _______  _______ _________ _______    _        _______  _______
// (  ___  )( \      (  ____ \(  ____ )\__   __/(  ____ \  ( \      (  ___  )(  ____ \
// | (   ) || (      | (    \/| (    )|   ) (   | (    \/  | (      | (   ) || (    \/
// | (___) || |      | (__    | (____)|   | |   | (_____   | |      | |   | || |
// |  ___  || |      |  __)   |     __)   | |   (_____  )  | |      | |   | || | ____
// | (   ) || |      | (      | (\ (      | |         ) |  | |      | |   | || | \_  )
// | )   ( || (____/\| (____/\| ) \ \__   | |   /\____) |  | (____/\| (___) || (___) |
// |/     \|(_______/(_______/|/   \__/   )_(   \_______)  (_______/(_______)(_______)

const getAlertsLog = async (
  gatewayGuid,
  deviceGuid,
  thresholdGuid,
  includeDeleted = false
) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "alerts-log?" +
    (gatewayGuid !== undefined ? "gatewayGuid=" + gatewayGuid + "&" : "") +
    (deviceGuid !== undefined ? "deviceGuid=" + deviceGuid + "&" : "") +
    (thresholdGuid !== undefined
      ? "thresholdGuid=" + thresholdGuid + "&"
      : "") +
    "includeDeleted=" +
    includeDeleted;

  return await _getWithIteration(url, 500);
};

const getAlertsLogByDeviceGuidsAndTimeSpan = async (
  deviceGuids = [],
  from,
  to,
  includeDeleted = false
) => {
  let url = BASE_API_URL + API_VERSION + "alerts-log/filter?";

  if (deviceGuids.length > 0) {
    let i = 0;

    for (let deviceGuid of deviceGuids) {
      url += (i > 0 ? "&" : "") + "deviceGuids=" + deviceGuid;
      i++;
    }

    url += "&";
  }

  let strFrom = utils.formatDate(from).replace(/:/g, "%3A");
  let strTo = utils.formatDate(to).replace(/:/g, "%3A");

  url +=
    "from=" + strFrom + "&to=" + strTo + "&includeDeleted=" + includeDeleted;

  return await _getWithIteration(url, 100);
};

const getAlertStatus = async (
  deviceGuid = null,
  gatewayGuid = null,
  thresholdGuid = null,
  customerGuid = null,
  status = null
) => {
  let url = BASE_API_URL + API_VERSION + "alerts-status";
  let first = true;
  for (const par of [
    { name: "deviceGuid", val: deviceGuid },
    { name: "gatewayGuid", val: gatewayGuid },
    { name: "thresholdGuid", val: thresholdGuid },
    { name: "customerGuid", val: customerGuid },
    { name: "status", val: status },
  ]) {
    if (par.val !== null) {
      if (first === true) {
        first = false;
        url += "?" + par.name + "=" + par.val;
      } else url += "&" + par.name + "=" + par.val;
    }
  }
  return await _getWithIteration(url, 200);
};

const getAlertInitialState = async () => {
  const response = await getAlertStatus(null, null, null, null, 1);
  const apiAlerts = requireList(response?.data?.data, "alerts", response);
  // Authoritative snapshot of active alarms grouped by gateway -> device.
  // No `total`: consumers derive the count from byId (single source of truth).
  const byId = {};
  for (const alert of apiAlerts) {
    const a = alert.threshold;
    a["alarmType"] = a.alertStatus;
    delete a.alertStatus;
    if (a.alarmType?.toUpperCase() !== "ON" || a.deletedAt !== null) continue;
    const { gatewayGuid, deviceGuid } = a;
    if (!byId[gatewayGuid]) byId[gatewayGuid] = {};
    if (!byId[gatewayGuid][deviceGuid]) byId[gatewayGuid][deviceGuid] = [];
    byId[gatewayGuid][deviceGuid].push(a);
  }
  return { byId, allIds: Object.keys(byId) };
};

const getLatestAlertLogByThresholdGuidAsync = async (
  thresholdGuid,
  alertEventType = 2
) => {
  const url = `${BASE_API_URL}${API_VERSION}alerts-log/latest/${thresholdGuid}?alertEventType=${alertEventType}`;
  let response = await callAPI(url);

  if (response.status === 200) {
    return response.data;
  }
  throw new Error("Error fetching latest alert log");
};

const getAlarms = async (params = {}) => {
  const {
    customerGuid,
    deviceGuids = [],
    gatewayGuids = [],
    siteGuids = [],
    dateFrom,
    dateTo,
    activeOnly = true
  } = params;

  let url = BASE_API_URL + API_VERSION + "alarm";
  const queryParams = [];

  // Add customerGuid if provided
  if (customerGuid) {
    queryParams.push(`customerGuid=${customerGuid}`);
  }

  // Add device GUIDs
  if (deviceGuids && deviceGuids.length > 0) {
    deviceGuids.forEach(guid => {
      queryParams.push(`deviceGuids=${guid}`);
    });
  }

  // Add gateway GUIDs
  if (gatewayGuids && gatewayGuids.length > 0) {
    gatewayGuids.forEach(guid => {
      queryParams.push(`gatewayGuids=${guid}`);
    });
  }

  // Add site GUIDs
  if (siteGuids && siteGuids.length > 0) {
    siteGuids.forEach(guid => {
      queryParams.push(`siteGuids=${guid}`);
    });
  }

  // Add date range
  if (dateFrom) {
    queryParams.push(`dateFrom=${encodeURIComponent(dateFrom)}`);
  }
  if (dateTo) {
    queryParams.push(`dateTo=${encodeURIComponent(dateTo)}`);
  }

  // Add activeOnly flag
  queryParams.push(`activeOnly=${activeOnly}`);

  // Construct final URL
  if (queryParams.length > 0) {
    url += "?" + queryParams.join("&");
  }

  return await callAPI(url);
};

//  _______  _______  _______  _______ _________ _______  _______ _________ _______  _        _______
// (  ____ )(  ____ \(  ____ )(       )\__   __/(  ____ \(  ____ \\__   __/(  ___  )( (    /|(  ____ \
// | (    )|| (    \/| (    )|| () () |   ) (   | (    \/| (    \/   ) (   | (   ) ||  \  ( || (    \/
// | (____)|| (__    | (____)|| || || |   | |   | (_____ | (_____    | |   | |   | ||   \ | || (_____
// |  _____)|  __)   |     __)| |(_)| |   | |   (_____  )(_____  )   | |   | |   | || (\ \) |(_____  )
// | (      | (      | (\ (   | |   | |   | |         ) |      ) |   | |   | |   | || | \   |      ) |
// | )      | (____/\| ) \ \__| )   ( |___) (___/\____) |/\____) |___) (___| (___) || )  \  |/\____) |
// |/       (_______/|/   \__/|/     \|\_______/\_______)\_______)\_______/(_______)|/    )_)\_______)

const createNewSubscriptionAdminOnly = async (ownerEmail, subscriptionName) => {
  let par = { email: ownerEmail, customerName: subscriptionName };
  return callAPI(BASE_API_URL + API_VERSION + "user/sub", API_POST, par)
    .then((response) => {
      return response;
    })
    .catch((error) => {
      return error;
    });
};

const getGatewayTwin = async (gatewayGuid) => {
  try {
    if (gatewayGuid) {
      let url =
        BASE_API_URL +
        API_VERSION +
        "gateway/gateway-twin?gatewayGuid=" +
        gatewayGuid;
      return await callAPI(url, API_GET, null, null, true);
    }
  } catch (e) {
    utils.debug("warn", "getGatewayTwin", e);
  }

  return null;
};

const getGatewayParameters = async (gatewayGuid) => {
  const url =
    BASE_API_URL + API_VERSION + "gateway/" + gatewayGuid + "/parameters";
  return await callAPI(url, API_GET, null, null, true);
};

const editGatewayParameters = async (gatewayGuid, parameters) => {
  const url =
    BASE_API_URL + API_VERSION + "gateway/" + gatewayGuid + "/parameters";
  return await callAPI(url, API_PUT, parameters, null, true);
};

// ______   _______          _________ _______  _______    ______   _______ _________ _______
// (  __  \ (  ____ \|\     /|\__   __/(  ____ \(  ____ \  (  __  \ (  ___  )\__   __/(  ___  )
// | (  \  )| (    \/| )   ( |   ) (   | (    \/| (    \/  | (  \  )| (   ) |   ) (   | (   ) |
// | |   ) || (__    | |   | |   | |   | |      | (__      | |   ) || (___) |   | |   | (___) |
// | |   | ||  __)   ( (   ) )   | |   | |      |  __)     | |   | ||  ___  |   | |   |  ___  |
// | |   ) || (       \ \_/ /    | |   | |      | (        | |   ) || (   ) |   | |   | (   ) |
// | (__/  )| (____/\  \   /  ___) (___| (____/\| (____/\  | (__/  )| )   ( |   | |   | )   ( |
// (______/ (_______/   \_/   \_______/(_______/(_______/  (______/ |/     \|   )_(   |/     \|

/**
 *
 * @param {*} devices
 * @param {*} siteGuid
 * @param {*} from
 * @param {*} to
 * @param {*} output
 * @param {*} organizationGuid
 * @param aggregated
 * @param dataType
 * @param samplingTime
 * @returns
 */
const getDeviceMeasure = async (
  devices = [],
  from = null,
  to = null,
  output = "xls",
  aggregated = false,
  dataType = 1,
  samplingTime = 15 * 60
) => {
  if (devices.length === 0) return null;
  let url = BASE_API_URL + API_VERSION + "device-data?";

  let i = 0;
  for (let device of devices) {
    url = url + (i === 0 ? "" : "&") + "devices=" + device;
    i++;
  }

  let strFrom = utils.formatDate(from).replace(/:/g, "%3A");
  let strTo = utils.formatDate(to).replace(/:/g, "%3A");

  url = url + "&from=" + strFrom;
  url = url + "&to=" + strTo;
  url = url + "&aggregated=" + aggregated;
  if (dataType) url = url + "&dataType=" + dataType;
  if (samplingTime) url = url + "&samplingTime=" + samplingTime;

  if (output !== "xls" && output !== "zip") url = url + "&output=xls";
  else url = url + "&output=" + output;

  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    responseType: "blob",
  };

  return await callAPIWithCancel(url, API_GET, null, config, true);
};

/**
 *
 * @param {*} siteGuid
 * @param {*} date
 * @param {*} highAlarm
 * @param {*} highWarning
 * @param {*} lowWarning
 * @param {*} lowAlarm
 * @param {*} output
 * @returns
 */
const getDeviceReport = async (
  siteGuid,
  date = null,
  highAlarm,
  highWarning,
  lowWarning,
  lowAlarm,
  foodQuality = 0,
  output = "pdf"
) => {
  if (siteGuid === null || siteGuid === "") return null;

  let url = BASE_API_URL + API_VERSION + "device-data?siteGuid=" + siteGuid;

  if (date !== null) url = url + "&date=" + date;

  if (
    foodQuality === 1 &&
    highAlarm !== null &&
    highWarning !== null &&
    lowWarning !== null &&
    lowAlarm !== null
  ) {
    url =
      url +
      "&highAlarm=" +
      highAlarm +
      "&highWarning=" +
      highWarning +
      "&lowAlarm=" +
      lowAlarm +
      "&lowWarning=" +
      lowWarning;
  }

  url = url + "&foodQualityReport=" + (foodQuality === 0 ? false : true);

  if (output !== "pdf" && output !== "xls") url = url + "&output=pdf";
  else url = url + "&output=" + output;

  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    responseType: "blob",
  };

  return await callAPIWithCancel(url, API_POST, null, config, true);
};

// _______  _______  _______  _       _________ _______  _        ______    _________ _______  _______  _        _______  _        _______ __________________ _______  _
// (  ____ \(  ____ )(  ___  )( (    /|\__   __/(  ____ \( (    /|(  __  \   \__   __/(  ____ )(  ___  )( (    /|(  ____ \( \      (  ___  )\__   __/\__   __/(  ___  )( (    /|
// | (    \/| (    )|| (   ) ||  \  ( |   ) (   | (    \/|  \  ( || (  \  )     ) (   | (    )|| (   ) ||  \  ( || (    \/| (      | (   ) |   ) (      ) (   | (   ) ||  \  ( |
// | (__    | (____)|| |   | ||   \ | |   | |   | (__    |   \ | || |   ) |     | |   | (____)|| (___) ||   \ | || (_____ | |      | (___) |   | |      | |   | |   | ||   \ | |
// |  __)   |     __)| |   | || (\ \) |   | |   |  __)   | (\ \) || |   | |     | |   |     __)|  ___  || (\ \) |(_____  )| |      |  ___  |   | |      | |   | |   | || (\ \) |
// | (      | (\ (   | |   | || | \   |   | |   | (      | | \   || |   ) |     | |   | (\ (   | (   ) || | \   |      ) || |      | (   ) |   | |      | |   | |   | || | \   |
// | )      | ) \ \__| (___) || )  \  |   | |   | (____/\| )  \  || (__/  )     | |   | ) \ \__| )   ( || )  \  |/\____) || (____/\| )   ( |   | |   ___) (___| (___) || )  \  |
// |/       |/   \__/(_______)|/    )_)   )_(   (_______/|/    )_)(______/      )_(   |/   \__/|/     \||/    )_)\_______)(_______/|/     \|   )_(   \_______/(_______)|/    )_)

/**
 * Get Languages List
 *
 * @return {*}
 */
const getLanguagesList = async () => {
  let url = BASE_API_URL + API_VERSION + "translations/list";

  return await callAPI(url, API_GET);
};

/**
 * Get Translation file
 *
 * @param {*} language
 * @return {*}
 */
const getTranslations = async (language) => {
  let url = BASE_API_URL + API_VERSION + "translations?language=" + language;

  return await callAPI(url, API_GET);
};

//           _______  _______  _______    _______ __________________ _______ _________ ______           _________ _______
// |\     /|(  ____ \(  ____ \(  ____ )  (  ___  )\__   __/\__   __/(  ____ )\__   __/(  ___ \ |\     /|\__   __/(  ____ \
// | )   ( || (    \/| (    \/| (    )|  | (   ) |   ) (      ) (   | (    )|   ) (   | (   ) )| )   ( |   ) (   | (    \/
// | |   | || (_____ | (__    | (____)|  | (___) |   | |      | |   | (____)|   | |   | (__/ / | |   | |   | |   | (__
// | |   | |(_____  )|  __)   |     __)  |  ___  |   | |      | |   |     __)   | |   |  __ (  | |   | |   | |   |  __)
// | |   | |      ) || (      | (\ (     | (   ) |   | |      | |   | (\ (      | |   | (  \ \ | |   | |   | |   | (
// | (___) |/\____) || (____/\| ) \ \__  | )   ( |   | |      | |   | ) \ \_____) (___| )___) )| (___) |   | |   | (____/\
// (_______)\_______)(_______/|/   \__/  |/     \|   )_(      )_(   |/   \__/\_______/|/ \___/ (_______)   )_(   (_______/

const editUserAttribute = async (userGuid, key, value) => {
  let url = BASE_API_URL + API_VERSION + "user-attribute";

  let par = { userGuid: userGuid, key: key, value: value };

  return await callAPI(url, API_POST, par);
};

//  _       _________ _______  _______  _        _______  _______
// ( \      \__   __/(  ____ \(  ____ \( (    /|(  ____ \(  ____ \
// | (         ) (   | (    \/| (    \/|  \  ( || (    \/| (    \/
// | |         | |   | |      | (__    |   \ | || (_____ | (__
// | |         | |   | |      |  __)   | (\ \) |(_____  )|  __)
// | |         | |   | |      | (      | | \   |      ) || (
// | (____/\___) (___| (____/\| (____/\| )  \  |/\____) || (____/\
// (_______/\_______/(_______/(_______/|/    )_)\_______)(_______/

///////////////////////////////////////
// A C T I V A T I O N   C O D E
///////////////////////////////////////

// Admin only
const getActivationCodes = async () => {
  let url = BASE_API_URL + API_VERSION + "activation-code?includeExpired=true";

  return await _getWithIteration(url);
};

// Admin only
const createActivationCode = async (soldFor, salesNote = null) => {
  return await callAPI(
    BASE_API_URL +
      API_VERSION +
      "activation-code?soldFor=" +
      soldFor +
      (salesNote ? "&salesNote=" + salesNote.replace(/\s/g, "%20") : ""),
    API_POST
  );
};

// Admin or user
const disableActivationCode = async (guid) => {
  if (guid === null) return null;

  let editPar = {
    uploadedAt: new Date().toISOString(),
    activationCodeGuid: guid,
  };

  let url = BASE_API_URL + API_VERSION + "activation-code";

  return await callAPI(url, API_PUT, editPar);
};

// Admin or user
const editActivationCode = async (guid, salesNote, soldFor) => {
  if (guid === null) return null;

  let editPar = {
    salesNote: salesNote,
    soldFor: soldFor,
    activationCodeGuid: guid,
  };

  let url = BASE_API_URL + API_VERSION + "activation-code";

  return await callAPI(url, API_PUT, editPar);
};

// Admin only
const deleteActivationCode = async (guid) => {
  let url = BASE_API_URL + API_VERSION + "activation-code/" + guid;

  return await callAPI(url, API_DELETE);
};

///////////////////////////////////////
// L I C E N S E
///////////////////////////////////////

const createLicense = async (
  activationCode = null,
  duration = null,
  licenseName = null,
  licenseFile = null
) => {
  if (!activationCode || !duration || (!licenseName && !licenseFile)) {
    return null;
  }

  let url =
    BASE_API_URL +
    API_VERSION +
    "license?activationCodeGuid=" +
    activationCode +
    "&duration=" +
    duration;

  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    headers: { ...config.headers, "Content-Type": "multipart/form-data" },
  };

  if (!licenseName) {
    return await callAPI(url, API_POST, licenseFile, config);
  } else {
    url += "&licenseName=" + licenseName;

    return await callAPI(url, API_POST, null, config);
  }
};

const getAvailableLicenses = async () => {
  let url = BASE_API_URL + API_VERSION + "license/list";

  return await callAPI(url);
};

const getThirdPartyDevices = async () => {
  let url = `${BASE_API_URL + API_VERSION}third-party-devices`;
  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return [];
  config = {
    ...config,
    headers: {
      ...config.headers,
      accept: "application/json",
      "Content-Type": "application/json",
    },
  };
  return await callAPI(url, API_GET, null, config);
};

///////////////////////////////////////
// B R I D G E
///////////////////////////////////////

const getLicensesByListOfSubscriptionGuids = async (subscriptionGuids = []) => {
  let url = BASE_API_URL + API_VERSION + "bridge";

  let i = 0;
  for (let guid of subscriptionGuids) {
    url += (i === 0 ? "?" : "&") + "subscriptionGuids=" + guid;
    i++;
  }

  return await _getWithIteration(url);
};

const addLicenseToSubscription = async (
  subscriptionGuids = [],
  licenseGuids = []
) => {
  if (subscriptionGuids.length === 0 || licenseGuids.length === 0) {
    return false;
  }

  let okResponses = 0;

  for (let licenseGuid of licenseGuids) {
    for (let subscriptionGuid of subscriptionGuids) {
      let url =
        BASE_API_URL +
        API_VERSION +
        "bridge?subscriptionGuid=" +
        subscriptionGuid +
        "&licenseGuid=" +
        licenseGuid;
      let response = await callAPI(url, API_POST);
      if (response.status === 201) okResponses++;
    }
  }

  return okResponses === licenseGuids.length * subscriptionGuids.length;
};

///////////////////////////////////////
// S U B S C R I P T I O N
///////////////////////////////////////

const getSubscriptionDetails = async (subscriptionGuid = null) => {
  let url = BASE_API_URL + API_VERSION + "subscriptions/" + subscriptionGuid;
  return await callAPI(url);
};


/**
 * Export organizations summary data
 * @param {string} [type="xlsx"] - Export file type
 * @returns {Promise<{blob: Blob, fileName: string|null}> | null}
 */
const exportOrganizationsSummary = async (type = "xlsx") => {
  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;

  config = {
    ...config,
    responseType: "blob",
  };

  const url =
    `${BASE_API_URL + API_VERSION}customer/summary?exportType=` +
    encodeURIComponent(type);
  const response = await callAPI(url, API_GET, null, config);

  if (!response) {
    return null;
  }

  const blob = response.data;
  const dispositionHeader =
    response.headers?.["content-disposition"] ||
    response.headers?.["Content-Disposition"] ||
    "";

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(dispositionHeader);
  const asciiMatch = /filename="?([^";]+)"?/i.exec(dispositionHeader);

  let fileName = null;

  if (utf8Match?.[1]) {
    try {
      fileName = decodeURIComponent(utf8Match[1]);
    } catch (error) {
      utils.debug(error, utils.MSG_TYPE_WARN);
    }
  }

  if (!fileName && asciiMatch?.[1]) {
    fileName = asciiMatch[1];
  }

  if (!fileName) {
    const extension = type || "xlsx";
    fileName = `summary.${extension}`;
  }

  return {
    blob,
    fileName,
  };
};

const getSubscriptions = async (organizationId = null) => {
  let url = BASE_API_URL + API_VERSION + "subscriptions";
  if (organizationId) url += "?organizationId=" + organizationId;
  return await callAPI(url);
};

const activateSubscription = async (subscriptionGuid, force = false) => {
  let url =
    BASE_API_URL +
    API_VERSION +
    "subscriptions/" +
    subscriptionGuid +
    "/activate";
  if (force) url += "?force=" + force;
  return await callAPI(url, API_POST, null, null, true);
};

//  _        _______ _________ _______
// | \    /\(  ____ )\__   __/(  ____ \
// |  \  / /| (    )|   ) (   | (    \/
// |  (_/ / | (____)|   | |   | (_____
// |   _ (  |  _____)   | |   (_____  )
// |  ( \ \ | (         | |         ) |
// |  /  \ \| )      ___) (___/\____) |
// |_/    \/|/       \_______/\_______)

const getKpi = async (id, message) => {
  let url = `${BASE_API_URL + API_VERSION}device-data/kpi/${id}`;

  let config = await getAPICallAuthHeader();
  if (!isAuthReady(config)) return null;
  config = {
    ...config,
    headers: {
      ...config.headers,
      accept: "application/json",
      "Content-Type": "application/json",
    },
  };

  return await callAPIWithCancel(
    url,
    API_POST,
    JSON.stringify(message),
    config
  );
};

//  ______   _______  _______           ______   _______  _______  _______  ______
// (  __  \ (  ___  )(  ____ \|\     /|(  ___ \ (  ___  )(  ___  )(  ____ )(  __  \
// | (  \  )| (   ) || (    \/| )   ( || (   ) )| (   ) || (   ) || (    )|| (  \  )
// | |   ) || (___) || (_____ | (___) || (__/ / | |   | || (___) || (____)|| |   ) |
// | |   | ||  ___  |(_____  )|  ___  ||  __ (  | |   | ||  ___  ||     __)| |   | |
// | |   ) || (   ) |      ) || (   ) || (  \ \ | |   | || (   ) || (\ (   | |   ) |
// | (__/  )| )   ( |/\____) || )   ( || )___) )| (___) || )   ( || ) \ \__| (__/  )
// (______/ |/     \|\_______)|/     \||/ \___/ (_______)|/     \||/   \__/(______/

const getDashboard = async (customerGuid = null) => {
  const endpoint = customerGuid
    ? `dashboard?customerGuid=${encodeURIComponent(customerGuid)}`
    : "dashboard";

  const url = `${BASE_API_URL}${API_VERSION}${endpoint}`;

  return await _getWithIteration(url, 100);
};

///////////////////////////////////////
// R E P O R T
///////////////////////////////////////

const getSiteAutomaticReport = async (siteGuid) => {
  return await callAPI(
    BASE_API_URL + API_VERSION + `site/${siteGuid}/automatic-report-config`,
    API_GET,
    null,
    null,
    true
  );
};

const upsertSiteAutomaticReport = ({
  siteGuid,
  time,
  reportType,
  emailList,
  isEnabled,
  fqrSettings,
  instantSend = false,
}) => {
  let par = {
    time,
    reportType,
    emailList,
    isEnabled,
    fqrSettings,
    instantSend,
  };
  return callAPI(
    BASE_API_URL + API_VERSION + `site/${siteGuid}/automatic-report-config`,
    API_PUT,
    par,
    null,
    true
  );
};

//  _______  _        _______  _______ _________
// (  ____ \( \      (  ____ \(  ____ \\__   __/
// | (    \/| (      | (    \/| (    \/   ) (   
// | (__    | |      | (__    | (__       | |   
// |  __)   | |      |  __)   |  __)      | |   
// | (      | |      | (      | (         | |   
// | )      | (____/\| (____/\| (____/\   | |   
// |/       (_______/(_______/(_______/   )_(   
                                             

// Super admin only: the organizations with the Fleet feature, each with its
// fleet counts, plus the totals over all of them. Resolves the raw response
// (data null when the token is not ready); rejects on an error status.
const getFleetEnabledOrganizationsAsync = async () =>
  callAPI(
    `${BASE_API_URL}${API_VERSION}customer/fleet-enabled`,
    API_GET,
    null,
    null,
    true
  );

const getFleetsAsync = async (customerGuid = null) => {
  const baseUrl = `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/fleets`;
  return await callAPI(baseUrl, API_GET, null, null, true);
};

const createFleetAsync = async (customerGuid, fleetName, parameterAccessLevel = null) => {
  const url = `${BASE_API_URL}${API_VERSION}fleet`;
  const body = {
    customerGuid,
    name: fleetName,
    parameterAccessLevel,
  };
  return await callAPI(url, API_POST, body);
}

const editFleetAsync = async (fleetGuid, fleetName, parameterAccessLevel = null) => {
  const url = `${BASE_API_URL}${API_VERSION}fleet/${fleetGuid}`;
  const body = {
    name: fleetName,
    parameterAccessLevel,
  };
  return await callAPI(url, API_PUT, body);
}

const deleteFleetAsync = async (fleetGuid) => {
  const url = `${BASE_API_URL}${API_VERSION}fleet/${fleetGuid}`;
  return await callAPI(url, API_DELETE);
}

const createAssetAsync = async (
  fleetGuid,
  macAddress,
  serialNumber,
  purchaseOrderCode,
  client,
  notes
) => {
  const url = `${BASE_API_URL}${API_VERSION}fleet/${fleetGuid}/assets`;
  const normalizedMacAddress = macAddress?.trim().toUpperCase();
  const body = {
    macAddress: normalizedMacAddress,
    serialNumber,
    purchaseOrderCode: purchaseOrderCode?.trim() || null,
    client: client?.trim() || null,
    notes: notes || null
  };
  return await callAPI(url, API_POST, body);
}

const updateAssetAsync = async (
  fleetGuid,
  assetGuid,
  serialNumber,
  purchaseOrderCode,
  client,
  notes
) => {
  const url = `${BASE_API_URL}${API_VERSION}fleet/${fleetGuid}/assets/${assetGuid}`;
  const body = {
    serialNumber,
    purchaseOrderCode: purchaseOrderCode?.trim() || null,
    client: client?.trim() || null,
    notes: notes || null
  };
  return await callAPI(url, API_PUT, body);
}

const deleteAssetAsync = async (fleetGuid, assetGuid) => {
  const url = `${BASE_API_URL}${API_VERSION}fleet/${fleetGuid}/assets/${assetGuid}`;
  return await callAPI(url, API_DELETE);
}

//  _______  _______ __________ _       _________ _______  _        _______  _        _______  _______  
// (       )(  ___  )\__   __/ ( (    /|\__   __/(  ____ \( (    /|(  ___  )( (    /|(  ____ \(  ____ \ 
// | () () || (   ) |   ) (    |  \  ( |   ) (   | (    \/|  \  ( || (   ) ||  \  ( || (    \/| (    \/ 
// | || || || (___) |   | |    |   \ | |   | |   | (__    |   \ | || (___) ||   \ | || |      | (__     
// | |(_)| ||  ___  |   | |    | (\ \) |   | |   |  __)   | (\ \) ||  ___  || (\ \) || |      |  __)    
// | |   | || (   ) |   | |    | | \   |   | |   | (      | | \   || (   ) || | \   || |      | (       
// | )   ( || )   ( |___) (___ | )  \  |   | |   | (____/\| )  \  || )   ( || )  \  || (____/\| (____/\ 
// |/     \||/     \|\_______/_|/    )_)   )_(   (_______/|/    )_)|/     \||/    )_)(_______/(_______/ 
//


const getActiveMaintenanceWindow = async (types = []) => {
  const params = new URLSearchParams();
  types.forEach((type) => params.append("status", type));
  const query = params.toString();
  const queryString = query ? "?" + query : "";
  const url = `${BASE_API_URL}${API_VERSION}maintenance${queryString}`;
  return await callAPI(url, API_GET, null, null, true);
};

// Subscription expiration notices for the given organizations (CS AB#49715).
// IoT.API answers only for the ones the user owns, so a member gets an empty list.
const getSystemMessages = async (organizationIds = []) => {
  const params = new URLSearchParams();
  organizationIds.forEach((id) => params.append("organizationId", id));
  const query = params.toString();
  const queryString = query ? "?" + query : "";
  const url = `${BASE_API_URL}${API_VERSION}system-messages${queryString}`;
  return await callAPI(url, API_GET, null, null, true);
};

const ackMaintenanceWindow = async (windowId, ackType) => {
  const url = `${BASE_API_URL}${API_VERSION}user-maintenance-ack`;
  return await callAPI(url, API_PUT, { maintenanceWindowGuid: windowId, ackType });
};

const getMaintenanceWindows = async (status = null, offset = 0, limit = DEFAULT_LIMIT) => {
  const params = new URLSearchParams();
  if (status) params.append("status", status);
  params.append("offset", offset);
  params.append("limit", limit);
  const url = `${BASE_API_URL}${API_VERSION}maintenance-window?${params.toString()}`;
  return await callAPI(url, API_GET, null, null, true);
};

const getMaintenanceWindow = async (guid) => {
  const url = `${BASE_API_URL}${API_VERSION}maintenance-window/${guid}`;
  return await callAPI(url, API_GET, null, null, true);
};

const createMaintenanceWindow = async (data) => {
  const url = `${BASE_API_URL}${API_VERSION}maintenance-window`;
  return await callAPI(url, API_POST, data);
};

const updateMaintenanceWindow = async (guid, data) => {
  const url = `${BASE_API_URL}${API_VERSION}maintenance-window/${guid}`;
  return await callAPI(url, API_PUT, data);
};

const deleteMaintenanceWindow = async (guid) => {
  const url = `${BASE_API_URL}${API_VERSION}maintenance-window/${guid}`;
  return await callAPI(url, API_DELETE, null);
};

// ---------------------------------------------------------------------------
// Chart templates (AB#49047) — named sets of chart variables, per organization.
//   GET    .../templates                  -> { total, items: [{ templateGuid, name, isPinned, selectedItemCount, createdAt, modifiedAt }] }
//   GET    .../templates/{guid}           -> { templateGuid, name, isPinned, items, selectedItemCount, createdAt, modifiedAt }
//   POST   .../templates                  <- { name, isPinned, items }  -> 201, body as the GET by id but without `items`
//   PUT    .../templates/{guid}           <- { name, items } (full replace; pinning is separate) -> 200
//   DELETE .../templates/{guid}           -> 204
//   PUT | DELETE .../templates/{guid}/pin -> 204 (idempotent)
// items: [{ code, color: "#rrggbb" | null, visible }]
// Every call is on `?kind=chart` and accepts `?deviceGuid=` (the device being
// looked at), see templatesUrl.
// Errors: 409 Template.NameAlreadyExists, 404 unknown / other org, 403 feature
// not enabled for the org. All calls skip the generic error toast: the
// caller decides (e.g. a 409 is shown inline in the name field).
// ---------------------------------------------------------------------------
// `deviceGuid` is the device the user is looking at: it lets IoT.API open the
// templates of `customerGuid` to a user whose permission comes from the
// organization owning the fleet that holds the device's asset.
// `kind` picks the template family: "chart" or "controller" (device templates).
const templatesUrl = (
  kind,
  customerGuid,
  templateGuid = null,
  suffix = "",
  deviceGuid = null,
  paging = null
) => {
  const query = new URLSearchParams({ kind });
  if (deviceGuid) query.set("deviceGuid", deviceGuid);
  if (paging) {
    query.set("limit", String(paging.limit));
    query.set("offset", String(paging.offset));
  }
  return (
    `${BASE_API_URL}${API_VERSION}customer/${customerGuid}/templates` +
    (templateGuid ? `/${templateGuid}` : "") +
    suffix +
    `?${query}`
  );
};

const chartTemplatesUrl = (customerGuid, templateGuid = null, suffix = "", deviceGuid = null) =>
  templatesUrl("chart", customerGuid, templateGuid, suffix, deviceGuid);

const getChartTemplates = async (customerGuid, deviceGuid = null) =>
  await callAPI(chartTemplatesUrl(customerGuid, null, "", deviceGuid), API_GET, null, null, true);

const getChartTemplate = async (customerGuid, templateGuid, deviceGuid = null) =>
  await callAPI(chartTemplatesUrl(customerGuid, templateGuid, "", deviceGuid), API_GET, null, null, true);

const createChartTemplate = async (customerGuid, data, deviceGuid = null) =>
  await callAPI(chartTemplatesUrl(customerGuid, null, "", deviceGuid), API_POST, data, null, true);

const updateChartTemplate = async (customerGuid, templateGuid, data, deviceGuid = null) =>
  await callAPI(chartTemplatesUrl(customerGuid, templateGuid, "", deviceGuid), API_PUT, data, null, true);

const deleteChartTemplate = async (customerGuid, templateGuid, deviceGuid = null) =>
  await callAPI(chartTemplatesUrl(customerGuid, templateGuid, "", deviceGuid), API_DELETE, null, null, true);

const pinChartTemplate = async (customerGuid, templateGuid, deviceGuid = null) =>
  await callAPI(chartTemplatesUrl(customerGuid, templateGuid, "/pin", deviceGuid), API_PUT, null, null, true);

const unpinChartTemplate = async (customerGuid, templateGuid, deviceGuid = null) =>
  await callAPI(chartTemplatesUrl(customerGuid, templateGuid, "/pin", deviceGuid), API_DELETE, null, null, true);

// ---------------------------------------------------------------------------
// Device templates (IoT.API "controller" templates): the variable codes a
// device's Variables page shows. Chart-template endpoints on ?kind=controller:
//   GET    .../templates?limit=&offset=  -> { total, items: [summary] } (pinned first)
//   GET    .../templates/{guid}          -> summary + items
//   POST   .../templates                 <- { name, isPinned, items } -> 201
//   PUT    .../templates/{guid}          <- { name, items } (full replace) -> 200
//   DELETE .../templates/{guid}          -> 204
//   PUT | DELETE .../templates/{guid}/pin -> 204
// items: [{ code }], 1 to 500, no duplicates.
// Applied template (one per device), device/{guid}/template?activeOrganization=:
//   PUT <- { templateGuid } -> 200 | POST (create and apply) <- { name, isPinned, items }
//   -> 201 | DELETE -> 204. PUT and POST answer with the applied template.
// GET device/{guid} adds appliedControllerTemplate (null when none) and
// canManageControllerTemplates.
// Errors: 400, 403, 404, 409 (name taken); no generic error toast.
// ---------------------------------------------------------------------------
const deviceTemplatesUrl = (customerGuid, templateGuid = null, suffix = "", deviceGuid = null, paging = null) =>
  templatesUrl("controller", customerGuid, templateGuid, suffix, deviceGuid, paging);

const appliedDeviceTemplateUrl = (deviceGuid, activeOrganizationGuid = null) =>
  `${BASE_API_URL}${API_VERSION}device/${deviceGuid}/template` +
  (activeOrganizationGuid
    ? `?activeOrganization=${encodeURIComponent(activeOrganizationGuid)}`
    : "");

const getDeviceTemplates = async (customerGuid, deviceGuid = null, paging = null) =>
  await callAPI(deviceTemplatesUrl(customerGuid, null, "", deviceGuid, paging), API_GET, null, null, true);

const getDeviceTemplate = async (customerGuid, templateGuid, deviceGuid = null) =>
  await callAPI(deviceTemplatesUrl(customerGuid, templateGuid, "", deviceGuid), API_GET, null, null, true);

const createDeviceTemplate = async (customerGuid, data, deviceGuid = null) =>
  await callAPI(deviceTemplatesUrl(customerGuid, null, "", deviceGuid), API_POST, data, null, true);

const updateDeviceTemplate = async (customerGuid, templateGuid, data, deviceGuid = null) =>
  await callAPI(deviceTemplatesUrl(customerGuid, templateGuid, "", deviceGuid), API_PUT, data, null, true);

const deleteDeviceTemplate = async (customerGuid, templateGuid, deviceGuid = null) =>
  await callAPI(deviceTemplatesUrl(customerGuid, templateGuid, "", deviceGuid), API_DELETE, null, null, true);

const pinDeviceTemplate = async (customerGuid, templateGuid, deviceGuid = null) =>
  await callAPI(deviceTemplatesUrl(customerGuid, templateGuid, "/pin", deviceGuid), API_PUT, null, null, true);

const unpinDeviceTemplate = async (customerGuid, templateGuid, deviceGuid = null) =>
  await callAPI(deviceTemplatesUrl(customerGuid, templateGuid, "/pin", deviceGuid), API_DELETE, null, null, true);

const applyDeviceTemplate = async (deviceGuid, templateGuid, activeOrganizationGuid = null) =>
  await callAPI(
    appliedDeviceTemplateUrl(deviceGuid, activeOrganizationGuid),
    API_PUT,
    { templateGuid },
    null,
    true
  );

const createAndApplyDeviceTemplate = async (deviceGuid, data, activeOrganizationGuid = null) =>
  await callAPI(appliedDeviceTemplateUrl(deviceGuid, activeOrganizationGuid), API_POST, data, null, true);

const removeAppliedDeviceTemplate = async (deviceGuid, activeOrganizationGuid = null) =>
  await callAPI(appliedDeviceTemplateUrl(deviceGuid, activeOrganizationGuid), API_DELETE, null, null, true);

//  _______  _______  _______  ______   _______  _______  _______          _________ _______  _______   
// (       )(  ____ \(       )(  ___ \ (  ____ \(  ____ )(  ____ \|\     /|\__   __/(  ____ )(  ____ \  
// | () () || (    \/| () () || (   ) )| (    \/| (    )|| (    \/| )   ( |   ) (   | (    )|| (    \/  
// | || || || (__    | || || || (__/ / | (__    | (____)|| (_____ | (___) |   | |   | (____)|| (_____   
// | |(_)| ||  __)   | |(_)| ||  __ (  |  __)   |     __)(_____  )|  ___  |   | |   |  _____)(_____  )  
// | |   | || (      | |   | || (  \ \ | (      | (\ (         ) || (   ) |   | |   | (            ) |  
// | )   ( || (____/\| )   ( || )___) )| (____/\| ) \ \__/\____) || )   ( |___) (___| )      /\____) |  
// |/     \|(_______/|/     \||/ \___/ (_______/|/   \__/\_______)|/     \|\_______/|/       \_______)  
//                                 

// IoT.API's memberships facade (AB#49867): what Access Rights used to ask core-IAM directly.
// Groups are addressed by guid; core-IAM's answers and errors come through unchanged.
const MEMBERSHIPS_URL = `${BASE_API_URL}${API_VERSION}memberships`;

const membershipsBodyOf = (response) => (response?.data === "" ? null : response?.data ?? null);

// A failed memberships call, carrying the fields callCoreIAM's rejections had: status, data (the
// error body as core-IAM or IoT.API wrote it) and responseString, which the callers show.
class MembershipsRequestError extends Error {
  constructor(status, data, responseString) {
    super(responseString);
    this.name = "MembershipsRequestError";
    this.status = status;
    this.data = data;
    this.responseString = responseString;
  }
}

// Keeps the shape of callCoreIAM, which the callers are written against: the body on success,
// null while the token is not ready, and { status, data, responseString } on failure. No toast:
// the callers show their own messages, and read core-IAM's errors from data.title / data.detail.
const callMemberships = async (callType, path, body = null) => {
  let response;
  try {
    response = await callAPI(`${MEMBERSHIPS_URL}/${path}`, callType, body, null, true);
  } catch (error_) {
    // callAPI rejects an answer >= 300 with the axios response, and a request that got no answer
    // at all with the error itself, which has no status.
    const status = error_?.status ?? null;
    throw new MembershipsRequestError(
      status,
      status === null ? null : membershipsBodyOf(error_),
      (error_?.statusText || status || error_?.message || "Unknown Error").toString()
    );
  }
  if (response.status === AUTH_NOT_READY_STATUS) return null;
  return membershipsBodyOf(response);
};

const getMyMemberships = () => callMemberships(API_GET, "me");

// The backend defaults every flag to false, so all three are always sent.
const getMembershipGroup = (
  groupGuid,
  { includeMembers = false, includeRoles = false, includeSubGroups = false } = {}
) =>
  callMemberships(
    API_GET,
    `${groupGuid}?includeMembers=${includeMembers}&includeRoles=${includeRoles}&includeSubGroups=${includeSubGroups}`
  );

// A group's members alone. core-IAM's users list called the user's id "id"; the group detail
// calls it "userId", so both are given.
const getGroupMembers = async (groupGuid) => {
  const group = await getMembershipGroup(groupGuid, { includeMembers: true });
  if (group === null) return null;
  return (group.members ?? []).map((member) => ({ ...member, id: member.userId }));
};

const searchMembershipUsers = () => callMemberships(API_GET, "users");

const addMembershipUsers = (groupGuid, roleId, emails = []) =>
  callMemberships(API_POST, `${groupGuid}?roleId=${roleId}`, emails);

const editMembershipRole = (groupGuid, userId, roleId, scope) =>
  callMemberships(API_PUT, `${groupGuid}/users/${userId}/role/${roleId}?scope=${scope}`);

// No options: core-IAM removes the users from the groups below, and from the parent when they
// belong to no other of its children.
const removeMembershipUsers = (groupGuid, emails = []) =>
  callMemberships(API_POST, `${groupGuid}/remove`, emails);

const backendService = {
  getAPICallAuthHeader,
  getAppConfigurationsAsync,
  getCustomersDetails,
  getCustomersInitialState,
  getCustomer,
  addCustomer,
  deleteCustomer,
  updateCustomer,
  getCustomerSubscription,
  getCustomerMqttApi,
  createCustomerMqttApiChannel,
  updateCustomerMqttApiChannel,
  deleteCustomerMqttApiChannel,
  assignCustomerMqttApiChannelFleet,
  unassignCustomerMqttApiChannelFleet,
  enableCustomerMqttApi,
  disableCustomerMqttApi,
  getPublicApiCallers,
  createPublicApiCaller,
  assignFleetToPublicApiCaller,
  unassignFleetFromPublicApiCaller,
  deletePublicApiCaller,
  regeneratePublicApiCallerKey,
  updatePublicApiCaller,
  suspendPublicApiCaller,
  activatePublicApiCaller,
  registerCustomerMqttApiCertificate,
  getSiteDetails,
  getSites,
  addSite,
  deleteSite,
  updateSite,
  getSiteLevelEmails,
  upsertSiteLevelEmails,
  updateSiteSettings,
  getUsersDetails,
  addUsers,
  deleteUser,
  getUserRole,
  deleteUserRole,
  assignRoleToUser,
  updateUserRole,
  getGatewayBrief,
  getGatewayByMAC,
  getGatewayDetails,
  getSingleGatewayDetail,
  addGateway,
  deleteGateway,
  updateGateway,
  refreshVars,
  changeGatewaySite,
  getGatewaysFull,
  updateParameterMap,
  parameterMap,
  createUpdateParFile,
  downloadMap,
  uploadMap,
  exportParametersList,
  getDevicesDetails,
  getDevice,
  getDevicesOnGateway,
  getDevicesOnSite,
  getDeviceMeasures,
  getLatestValidMeasure,
  getDeviceWelTranslationFiles,
  getDeviceWelTranslationFileDetails,
  addUpdateDeviceWelTranslationFile,
  deleteDeviceWelTranslationFile,
  getDeviceWelFiles,
  getDeviceWelFileDetails,
  addUpdateDeviceWelFile,
  deleteDeviceWelFile,
  getDeviceRuntimeCommandFiles,
  getDeviceRuntimeCommandFileDetails,
  addUpdateDeviceRuntimeCommandFile,
  deleteDeviceRuntimeCommandFile,
  getDeviceTypeFiles,
  getDeviceTypeFileDetails,
  addUpdateDeviceTypeFile,
  deleteDeviceTypeFile,
  getDeviceDSCList,
  getGatewayInitialState,
  getSiteInitialState,
  sendMessageToGateway,
  getAllThresholdDetails,
  setDefaultThresholds,
  createNewThresholds,
  editExistingThreshold,
  getAllThresholdDetailsFull,
  getThresholdDetailsByGuid,
  deleteThreshold,
  getAllThresholdNotify,
  createOrUpdateThresholdNotify,
  deleteThresholdNotify,
  deleteAllThresholdNotifyForThreshold,
  getAlertsLog,
  getAlertsLogByDeviceGuidsAndTimeSpan,
  getAlertStatus,
  getAlertInitialState,
  getAlarms,
  getLatestAlertLogByThresholdGuidAsync,
  updateDevice,
  updateDeviceCustomizations,
  createNewSubscriptionAdminOnly,
  getDevicesFull,
  getGatewayTwin,
  wipeGatewayConfigAsync,
  getGatewayParameters,
  editGatewayParameters,
  getDeviceMeasure,
  getDeviceReport,
  getLanguagesList,
  getTranslations,
  editUserAttribute,
  getActivationCodes,
  disableActivationCode,
  editActivationCode,
  deleteActivationCode,
  createActivationCode,
  createLicense,
  getAvailableLicenses,
  getThirdPartyDevices,
  getLicensesByListOfSubscriptionGuids,
  addLicenseToSubscription,
  getKpi,
  getDashboard,
  getSiteAutomaticReport,
  upsertSiteAutomaticReport,
  getSubscriptionDetails,
  getSubscriptions,
  exportOrganizationsSummary,
  activateSubscription,
  getFleetsAsync,
  createFleetAsync,
  editFleetAsync,
  deleteFleetAsync,
  createAssetAsync,
  updateAssetAsync,
  deleteAssetAsync,
  getConfigurationAsync,
  getRestApiDefinitionAsync,
  getApiEnabledOrganizationsAsync,
  getFleetEnabledOrganizationsAsync,
  getActiveMaintenanceWindow,
  getSystemMessages,
  ackMaintenanceWindow,
  getMaintenanceWindows,
  getMaintenanceWindow,
  createMaintenanceWindow,
  updateMaintenanceWindow,
  deleteMaintenanceWindow,
  getChartTemplates,
  getChartTemplate,
  createChartTemplate,
  updateChartTemplate,
  deleteChartTemplate,
  pinChartTemplate,
  unpinChartTemplate,
  getDeviceTemplates,
  getDeviceTemplate,
  createDeviceTemplate,
  updateDeviceTemplate,
  deleteDeviceTemplate,
  pinDeviceTemplate,
  unpinDeviceTemplate,
  applyDeviceTemplate,
  createAndApplyDeviceTemplate,
  removeAppliedDeviceTemplate,
  getMyMemberships,
  getMembershipGroup,
  getGroupMembers,
  searchMembershipUsers,
  addMembershipUsers,
  editMembershipRole,
  removeMembershipUsers,
};

export default backendService;

const callAPI = async (
  url,
  callType = API_GET,
  par = null,
  config = null,
  skipAlert = false
) => {
  let conf = null;
  let i = 0;
  if (config !== null) conf = config;
  else {
    while (conf === null && i < 10) {
      conf = await getAPICallAuthHeader();
      if (!isAuthReady(conf)) return authNotReadyResponse();
      i++;
    }
  }
  conf.validateStatus = false;

  return new Promise(async (resolve, reject) => {
    try {
      let res = null;
      switch (callType) {
        case API_GET:
          res = await axios.get(url, conf);
          break;
        case API_POST:
          res = await axios.post(url, par, conf);
          break;
        case API_PUT:
          res = await axios.put(url, par, conf);
          break;
        case API_PATCH:
          res = await axios.patch(url, par, conf);
          break;
        case API_DELETE:
          res =
            par == null
              ? await axios.delete(url, conf)
              : await axios.delete(url, { ...conf, data: par });
          break;
        default:
          let error = {
            data: "Invalid function type",
            status: 404,
          };
          reject(error);
      }
      if (res.status === 401 && isSessionRevoked(res)) {
        handleSessionRevoked();
        reject(res);
      } else if (res.status >= 300) {
        if (!skipAlert) {
          notifyStore?.addNotification({
            ...tempOptions,
            content: NotifyContent(
              "danger",
              null,
              (res.data.detail ?? res.data) + " - " + res.status
            ),
          });
        }
        reject(res);
      } else {
        resolve(res);
      }
    } catch (error) {
      if (!skipAlert) {
        notifyStore.addNotification({
          ...tempOptions,
          content: NotifyContent(
            "danger",
            null,
            error.response === undefined
              ? error.message
              : error.response.statusText
          ),
        });
      }
      reject(error);
    }
  });
};

export let sources = {};

const callAPIWithCancel = async (
  url,
  callType = API_GET,
  par = null,
  config = null,
  skipAlert = false
) => {
  let conf = null;
  let i = 0;
  if (config !== null) conf = config;
  else {
    while (conf === null && i < 10) {
      conf = await getAPICallAuthHeader();
      // Same no-result contract as callAPI, wrapped in the { execute, id }
      // shape callers destructure — `await response.execute` must still work.
      if (!isAuthReady(conf))
        return { execute: Promise.resolve(authNotReadyResponse()), id: null };
      i++;
    }
  }

  let source = axios.CancelToken.source();
  conf.cancelToken = source.token;
  conf.validateStatus = false;

  let id = Math.max(Object.keys(sources)) + 1 || 1;

  sources[id] = { source: source };

  return {
    execute: new Promise(async (resolve, reject) => {
      try {
        let res = null;
        switch (callType) {
          case API_GET:
            res = await axios.get(url, conf);
            break;
          case API_POST:
            res = await axios.post(url, par, conf);
            break;
          case API_PUT:
            res = await axios.put(url, par, conf);
            break;
          case API_DELETE:
            res = await axios.delete(url, conf);
            break;
          default:
            let error = {
              data: "Invalid function type",
              status: 404,
            };
            reject(error);
        }
        if (res.status === 401 && isSessionRevoked(res)) {
          handleSessionRevoked();
          reject(res);
        } else if (res.status >= 300) {
          if (!skipAlert) {
            notifyStore.addNotification({
              ...tempOptions,
              content: NotifyContent(
                "danger",
                null,
                res.data + " - " + res.status
              ),
            });
          }
          reject(res);
        } else {
          resolve(res);
        }
      } catch (error) {
        if (error.toString() !== "Cancel" && !skipAlert) {
          notifyStore.addNotification({
            ...tempOptions,
            content: NotifyContent(
              "danger",
              null,
              error.response === undefined
                ? error.message
                : error.response.statusText
            ),
          });
        }
        reject(error);
      }
      delete sources[id];
    }),
    id: id,
  };
};
